import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    meseDi, oraIta as oraItaliana, dataOraIta, perCampoItaliano, meseClinica,
} from '../utils/oraItaliana';
import {
    Clock, FileText, Users, Download, Plus, X, Check, AlertTriangle,
    Pencil, Trash2, ShieldAlert, History, CalendarOff, KeyRound, Tablet,
    Lock, Unlock, Timer, ThumbsUp, ThumbsDown, Send, Hourglass,
} from 'lucide-react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { personaleAPI, presenzeAPI, bustePagaAPI } from '../services/personaleApi';
import { LeMieBustePaga, GestioneBustePaga } from '../components/personale/BustePaga';
import { useAuthStore } from '../store/useStore';
import { useTrattieniVersione } from '../utils/versioneInLinea';
import IntestazioneSezione from '../components/comuni/IntestazioneSezione';
import ErroreLettura from '../components/comuni/ErroreLettura';
import { conUnTentativo, motivoDellaLettura } from '../utils/primaLettura';
import CampoMeseAnno, { aIso, daIso } from '../components/CampoMeseAnno';
import {
    MieCorrezioni, ModuloCorrezione, RichiesteCorrezione, finestraRichieste,
} from '../components/presenze/CorrezioniTimbrature';
import MieTimbrature from '../components/presenze/MieTimbrature';
import '../styles/AreaDipendenti.css';

/**
 * AreaDipendenti — documenti personali, timbratura, presenze.
 *
 * DUE PAGINE IN UNA, PERCHE' SONO LA STESSA COSA VISTA DA DUE POSTI
 * Un dipendente vede il pulsante per timbrare, le proprie ore e le
 * proprie buste paga. L'admin vede tutto l'organico, interviene sul
 * registro e produce il file per lo studio paghe.
 *
 * Il confine NON lo decide questa schermata: lo decide il server, che
 * filtra per identita' su ogni rotta. Qui si nasconde solo cio' che
 * sarebbe un pulsante destinato a rispondere 403 — nascondere una voce
 * non e' protezione, e non e' quello che sta facendo.
 */

const MESI = ['', 'gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno',
              'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];

const TIPI_DOC = [
    { v: 'busta_paga',    t: 'Busta paga' },
    { v: 'cu',            t: 'CU / Certificazione Unica' },
    { v: 'contratto',     t: 'Contratto' },
    { v: 'comunicazione', t: 'Comunicazione' },
    { v: 'altro',         t: 'Altro' },
];

const nomeTipo = (v) => (TIPI_DOC.find((x) => x.v === v) || {}).t || v;

/* Buste paga, CU e contratti (migration 341) hanno la loro strada,
   «Buste paga», sotto un permesso dedicato: da «Documenti» non si caricano
   piu'. L'elenco vero e' nel database (documento_dipendente_riservato); qui
   serve solo a non offrirli nel modulo e a non mostrarli due volte. */
const TIPI_RISERVATI = ['busta_paga', 'cu', 'contratto'];
const TIPI_DOC_GENERICI = TIPI_DOC.filter((t) => !TIPI_RISERVATI.includes(t.v));

const dataIta = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(d) ? '—' : d.toLocaleDateString('it-IT');
};

const oraIta = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(d) ? '—' : d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
};

/** Ore in decimale con la virgola, come le vuole lo studio paghe. */
const ore = (v) => {
    const n = parseFloat(v);
    if (!Number.isFinite(n) || n === 0) return '—';
    return n.toLocaleString('it-IT', { maximumFractionDigits: 2 });
};

/** Minuti in "6h15" o "40 min". Sulle eccedenze si parla in minuti, non
 *  in ore decimali: «0,67 ore di straordinario» non vuol dire niente a
 *  nessuno, «40 minuti» si'. */
const durata = (m) => {
    const n = parseInt(m, 10);
    if (!Number.isFinite(n) || n === 0) return '—';
    const segno = n < 0 ? '-' : '';
    const a = Math.abs(n);
    const h = Math.floor(a / 60);
    const r = a % 60;
    return h ? `${segno}${h}h${String(r).padStart(2, '0')}` : `${segno}${r} min`;
};

/** Minuti in «3:56» (ore:minuti, i minuti sempre a due cifre): il numero
 *  piccolo della casella del giorno, accanto al «+» dello straordinario da
 *  autorizzare (Alex, 2/11/2026: «in ore e minuti, +3:56»). Fino a quel
 *  giorno erano i minuti nudi («+236»), che nella casella sembravano ore. */
const oreEMinuti = (m) => {
    const n = Math.max(0, parseInt(m, 10) || 0);
    return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`;
};

/** Come durata(), ma lo zero si scrive «0 min»: nelle quattro voci delle
 *  ore (ordinarie, straordinario, mancanti) zero e' un valore, non un vuoto. */
const minutiVoce = (m) => ((parseInt(m, 10) || 0) === 0 ? '0 min' : durata(m));

/* Le etichette degli stati della giornata. Il server manda il codice, non
   la parola: un'etichetta scritta a mano in tre schermate diventa tre
   etichette diverse, e questa e' l'unica copia nel frontend. */
const STATI = {
    regolare:                 { t: 'Regolare',                   c: 'buono' },
    sotto:                    { t: 'Sotto l\'orario',            c: 'attesa' },
    eccedenza_autorizzata:    { t: 'Straordinario autorizzato',  c: 'buono' },
    eccedenza_da_autorizzare: { t: 'Da autorizzare',             c: 'attesa' },
    incompleta:               { t: 'Manca una timbratura',       c: 'guasto' },
    assenza:                  { t: 'Assenza',                    c: '' },
    festivo:                  { t: 'Festivo',                    c: '' },
    festivo_lavorato:         { t: 'Festivo lavorato',           c: '' },
    domenica:                 { t: 'Domenica',                   c: '' },
    domenica_lavorata:        { t: 'Domenica lavorata',          c: '' },
    non_lavorato:             { t: 'Non lavorato',               c: '' },
};

/** Il giorno «corretto» a parole (Alex, 22/10/2026; migration 339): chi ha
 *  chiesto, chi ha approvato, quando. Lo calcola il server
 *  (giornate_corrette), qui si scrive soltanto. */
const testoCorretta = (c) => {
    /* Le stesse parole della nota dell'.xlsx (notaCorretta nel backend). */
    const quando = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}:\d{2})$/.exec(String(c.approvata_il || ''));
    const chi = [c.da_richiesta > 0 ? c.chiesta_da : null,
                 c.dirette > 0 ? 'amministrazione (correzione diretta)' : null]
        .filter(Boolean).join(', ');
    return `Giornata corretta: chiesta da ${chi || '—'}, approvata da ${c.approvata_da || '—'}`
         + (quando ? ` il ${quando[3]}/${quando[2]}/${quando[1]} ${quando[4]}` : '');
};

const STATI_ASSENZA = {
    richiesta:  { t: 'In attesa',  c: 'attesa' },
    approvata:  { t: 'Approvata',  c: 'buono' },
    respinta:   { t: 'Respinta',   c: 'guasto' },
    annullata:  { t: 'Ritirata',   c: '' },
};

const messaggio = (e, ripiego) =>
    e?.response?.data?.message || e?.message || ripiego;

/** Primo e ultimo giorno del mese corrente, in formato ISO. */
/* Fino al 7/10/2026 era new Date(anno, mese, 1).toISOString(): su una
   postazione in Italia il giorno prima, e il mese andava dal 30 al 30. */
function meseCorrente() {
    return meseDi();
}

const AreaDipendenti = () => {
    const utente = useAuthStore((s) => s.user);
    const eAdmin = utente?.ruolo === 'admin';

    /* «Le mie timbrature» (Alex, 28/10/2026) tiene linguetta e mese
       nell'indirizzo (?vista=timbrature&mese=AAAA-MM): una ricarica, o la
       versione nuova che arriva da sola, li ritrova. */
    const [parametri, setParametri] = useSearchParams();
    const [vista, setVista] = useState(() => (parametri.get('vista') === 'timbrature'
        ? 'timbrature' : (eAdmin ? 'presenze' : 'mie')));
    const [errore, setErrore] = useState(null);
    const [avviso, setAvviso] = useState(null);
    const [inCorso, setInCorso] = useState(false);

    const [periodo, setPeriodo] = useState(meseCorrente());
    const [stato, setStato] = useState(null);
    const [dipendenti, setDipendenti] = useState([]);
    const [giornate, setGiornate] = useState([]);
    const [avvisoPresenze, setAvvisoPresenze] = useState(null);
    const [documenti, setDocumenti] = useState([]);
    /* Buste paga (341): il modulo acceso, il permesso, l'organico, dal server. */
    const [statoBuste, setStatoBuste] = useState(null);
    const [causali, setCausali] = useState([]);
    const [assenze, setAssenze] = useState([]);
    const [interventi, setInterventi] = useState([]);
    const [rifiuti, setRifiuti] = useState([]);
    const [candidati, setCandidati] = useState([]);

    const [pannello, setPannello] = useState(null);
    const [formDoc, setFormDoc] = useState(null);
    const [formAssenza, setFormAssenza] = useState(null);
    const [formDip, setFormDip] = useState(null);
    const [formTimbratura, setFormTimbratura] = useState(null);
    /* Il dettaglio della giornata aperto da un clic sulla griglia (Alex,
       7/10/2026): { dipendente_id, nominativo, giorno, dati, errore }. Resta
       anche mentre il modulo di una timbratura e' aperto sopra, cosi' dopo
       l'intervento si torna li' e si rilegge. */
    const [giornataAperta, setGiornataAperta] = useState(null);
    const [accessi, setAccessi] = useState(null);
    const [statoPin, setStatoPin] = useState(null);
    const [formPin, setFormPin] = useState(null);
    const [pinGenerato, setPinGenerato] = useState(null);
    /* Il PIN non si mostra piu' dopo: finche' e' a video la pagina non si
       aggiorna da sola (utils/versioneInLinea.js). */
    useTrattieniVersione(!!pinGenerato);
    const [dispositivi, setDispositivi] = useState([]);
    const [impostazioni, setImpostazioni] = useState(null);

    const [pendenti, setPendenti] = useState({ richieste: [], quante: 0, gia_passate: 0 });
    const [straordinari, setStraordinari] = useState([]);
    const [chiusure, setChiusure] = useState([]);
    const [anteprima, setAnteprima] = useState(null);
    const [formRichiesta, setFormRichiesta] = useState(null);
    const [formDecisione, setFormDecisione] = useState(null);
    const [formStraordinario, setFormStraordinario] = useState(null);
    const [meseDaChiudere, setMeseDaChiudere] = useState(() =>
        meseClinica());
    /* Il mese come lo scrive chi lo scrive (CampoMeseAnno): «09/2026». Il
       mese vero cambia solo quando il testo e' completo e valido. */
    const [testoMeseChiusura, setTestoMeseChiusura] = useState(() => daIso(meseClinica()));
    /* Le correzioni delle proprie timbrature (Alex, 22/10/2026; migration
       339): le proprie richieste, il modulo aperto ({ giorno }) e la
       richiesta da mettere in evidenza quando si arriva da un avviso. */
    const [mieCorr, setMieCorr] = useState({ richieste: [], entro_giorni: null, caricate: false });
    const [moduloCorr, setModuloCorr] = useState(null);
    const [evidenzaCorr, setEvidenzaCorr] = useState(null);
    const [arrivoCorr, setArrivoCorr] = useState(false);
    const [formGiorniCorr, setFormGiorniCorr] = useState(null);

    /* ARRIVO DA UN COMPITO.
       Il compito notturno delle presenze porta il dipendente e il mese
       (utils/destinazione.js): si apre sulla scheda giusta, sul periodo
       giusto e su quella persona sola. Senza, chi legge il compito deve
       rifare a mano tre scelte, e tre passaggi fra il leggere e l'agire
       sono tre occasioni per rimandare. */
    const posizione = useLocation();
    const daCompito = posizione.state;

    /* L'indirizzo segue la linguetta: si scrive sostituendo, senza un passo
       di cronologia per clic, e lo stato dell'arrivo da un compito resta. */
    const meseTimbrature = parametri.get('mese') || '';
    const scriviIndirizzo = (vistaNuova, meseNuovo) => {
        const p = new URLSearchParams(parametri);
        if (vistaNuova === 'timbrature') {
            p.set('vista', 'timbrature');
            if (meseNuovo) p.set('mese', meseNuovo); else p.delete('mese');
        } else {
            p.delete('vista');
            p.delete('mese');
        }
        if (p.toString() !== parametri.toString()) {
            setParametri(p, { replace: true, state: posizione.state });
        }
    };
    useEffect(() => {
        scriviIndirizzo(vista, vista === 'timbrature' ? meseTimbrature : '');
        // eslint-disable-next-line
    }, [vista]);
    const [soloDipendente, setSoloDipendente] = useState(
        () => daCompito?.dipendenteId || null);

    /* Si applica UNA volta, all'arrivo: se girasse a ogni render
       riporterebbe la schermata al punto di partenza ogni volta che
       l'utente cambia periodo, e sembrerebbe rotta. */
    useEffect(() => {
        if (!daCompito) return;
        if (daCompito.vista) setVista(daCompito.vista);
        if (/^\d{4}-\d{2}$/.test(daCompito.mese || '')) {
            const [a, m] = daCompito.mese.split('-').map(Number);
            const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
                             + `-${String(d.getDate()).padStart(2, '0')}`;
            setPeriodo({ dal: iso(new Date(a, m - 1, 1)), al: iso(new Date(a, m, 0)) });
        }
        // eslint-disable-next-line
    }, []);

    /* ---------- caricamenti ----------
       Le letture con cui la schermata si apre riprovano da sole una volta se
       la risposta non arriva (il fermo di un rilascio di giorno,
       primaLettura.js). Gli errori delle letture che lo dicono stanno qui,
       uno per lettura: la lettura riuscita toglie il suo, e «Riprova» rifa'
       quelle non riuscite. Gli errori dei salvataggi restano in `errore`. */
    const [erroriLettura, setErroriLettura] = useState({});
    const esitoLettura = useCallback((chiave, testo) => setErroriLettura(
        (x) => ((x[chiave] || null) === testo ? x : { ...x, [chiave]: testo })), []);

    const caricaStato = useCallback(async () => {
        try {
            const r = await conUnTentativo(() => presenzeAPI.stato());
            setStato(r.data.data);
        } catch (e) { /* non blocca il resto */ }
    }, []);

    const caricaGiornate = useCallback(async () => {
        try {
            const r = await conUnTentativo(() => presenzeAPI.giornate(periodo.dal, periodo.al));
            setGiornate(r.data.data.dipendenti || []);
            esitoLettura('giornate', null);
            /* Quante righe non sono state collegate a un'anagrafica. Il
               server le manda lo stesso, con un segnaposto: un export che
               omette una riga senza dirlo e' un totale sbagliato in mano
               a chi lo legge. */
            setAvvisoPresenze(r.data.data.avviso || null);
        } catch (e) {
            esitoLettura('giornate', motivoDellaLettura(e, 'Impossibile caricare le presenze'));
        }
    }, [periodo, esitoLettura]);

    const caricaStraordinari = useCallback(async () => {
        try {
            const r = await conUnTentativo(() => presenzeAPI.straordinari(periodo.dal, periodo.al));
            setStraordinari(r.data.data.straordinari || []);
        } catch (e) { /* non blocca il resto della schermata */ }
    }, [periodo]);

    const caricaPendenti = useCallback(async () => {
        if (!eAdmin) return;
        try {
            const r = await conUnTentativo(() => presenzeAPI.richiestePendenti());
            setPendenti(r.data.data);
        } catch (e) { /* il contatore puo' mancare senza rompere niente */ }
    }, [eAdmin]);

    const caricaAssenze = useCallback(async () => {
        try {
            const r = await conUnTentativo(() => presenzeAPI.assenze(periodo.dal, periodo.al));
            setAssenze(r.data.data.assenze || []);
        } catch (e) { /* non blocca il resto */ }
    }, [periodo]);

    const caricaDocumenti = useCallback(async () => {
        try {
            const r = await conUnTentativo(() => personaleAPI.documenti());
            setDocumenti(r.data.data.documenti || []);
            esitoLettura('documenti', null);
        } catch (e) {
            esitoLettura('documenti', motivoDellaLettura(e, 'Impossibile caricare i documenti'));
        }
    }, [esitoLettura]);

    useEffect(() => { caricaStato(); caricaDocumenti(); }, [caricaStato, caricaDocumenti]);
    useEffect(() => {
        conUnTentativo(() => bustePagaAPI.stato()).then((r) => setStatoBuste(r.data.data)).catch(() => setStatoBuste(null));
    }, []);
    const errBuste = useCallback((m) => setErrore(m), []);
    const avvisoBuste = useCallback((m) => setAvviso(m), []);

    const caricaMieCorrezioni = useCallback(async () => {
        try {
            const r = await conUnTentativo(() => presenzeAPI.mieCorrezioni());
            setMieCorr({ richieste: r.data.data.richieste || [],
                         entro_giorni: r.data.data.entro_giorni ?? null, caricate: true });
        } catch (e) {
            setMieCorr((x) => ({ ...x, caricate: true }));
        }
    }, []);
    useEffect(() => { if (stato?.in_organico) caricaMieCorrezioni(); }, [stato?.in_organico, caricaMieCorrezioni]);

    /* ARRIVO DA UN AVVISO SU UNA RICHIESTA DI CORREZIONE: la destinazione
       (utils/destinazione.js) porta solo l'id. Se la richiesta e' fra le
       proprie si apre «Le mie ore» (la dipendente rifiutata, anche se e'
       amministratore); altrimenti, all'amministrazione, le richieste da
       decidere in Presenze. Di chi e' la richiesta lo dice il server con
       l'elenco delle proprie, non questa schermata. Una volta sola. */
    useEffect(() => {
        const id = daCompito?.richiestaCorrezione;
        if (!id || arrivoCorr || !stato) return;
        if (stato.in_organico && !mieCorr.caricate) return;
        setArrivoCorr(true);
        setEvidenzaCorr(Number(id));
        if (mieCorr.richieste.some((r) => r.id === Number(id))) setVista('mie');
        else if (eAdmin) setVista('presenze');
        // eslint-disable-next-line
    }, [stato, mieCorr, arrivoCorr]);

    const ritiraCorrezione = async (r) => {
        if (!window.confirm(`Ritirare la richiesta di correzione del ${dataIta(`${r.giorno}T12:00:00`)}?`)) return;
        try {
            setErrore(null);
            const x = await presenzeAPI.ritiraCorrezione(r.id);
            setAvviso(x.data.message);
            await caricaMieCorrezioni();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nel ritiro'));
            await caricaMieCorrezioni();
        }
    };

    const correzioneInviata = async (testo) => {
        setModuloCorr(null);
        setAvviso(testo);
        await caricaMieCorrezioni();
    };

    const salvaGiorniCorr = async () => {
        try {
            setErrore(null);
            setInCorso(true);
            const r = await presenzeAPI.salvaImpostazioni({
                richieste_giorni: parseInt(formGiorniCorr.giorni, 10),
            });
            setAvviso(r.data.message);
            setFormGiorniCorr(null);
            await caricaDispositivi();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nel salvataggio'));
        } finally {
            setInCorso(false);
        }
    };

    useEffect(() => {
        conUnTentativo(() => presenzeAPI.statoPin()).then((r) => setStatoPin(r.data.data)).catch(() => {});
    }, []);
    useEffect(() => { caricaGiornate(); }, [caricaGiornate]);

    useEffect(() => {
        conUnTentativo(() => presenzeAPI.causali()).then((r) => setCausali(r.data.data.causali || [])).catch(() => {});
        if (eAdmin) {
            conUnTentativo(() => personaleAPI.dipendenti())
                .then((r) => setDipendenti(r.data.data.dipendenti || [])).catch(() => {});
        }
    }, [eAdmin]);

    useEffect(() => { caricaAssenze(); }, [caricaAssenze]);
    useEffect(() => { caricaStraordinari(); }, [caricaStraordinari]);
    useEffect(() => { caricaPendenti(); }, [caricaPendenti]);

    /* ---------- PIN ----------
       La timbratura da qui non c'e': si fa dal tablet, con la chiave del
       dispositivo e il PIN. Un pulsante "timbra" dietro il solo login
       permetterebbe di timbrare da casa. Qui si gestisce il PIN, che e'
       la propria meta' della chiave. */

    const salvaPin = async () => {
        try {
            setErrore(null);
            setInCorso(true);
            await presenzeAPI.cambiaPin(formPin.attuale, formPin.nuovo);
            setAvviso('PIN aggiornato. Usalo sul tablet per timbrare.');
            setFormPin(null);
            setPannello(null);
            const r = await presenzeAPI.statoPin();
            setStatoPin(r.data.data);
        } catch (e) {
            setErrore(messaggio(e, 'Errore nel cambio del PIN'));
        } finally {
            setInCorso(false);
        }
    };

    /* ---------- tablet ---------- */

    const caricaDispositivi = useCallback(async () => {
        try {
            const [d, i] = await Promise.all([
                personaleAPI.dispositivi(),
                presenzeAPI.impostazioni(),
            ]);
            setDispositivi(d.data.data.dispositivi || []);
            setImpostazioni(i.data.data);
        } catch (e) {
            setErrore(messaggio(e, 'Impossibile caricare i dispositivi'));
        }
    }, []);

    const attivaRilevazione = async (valore) => {
        try {
            setErrore(null);
            const r = await presenzeAPI.salvaImpostazioni({ attiva: valore });
            setAvviso(r.data.message);
            await caricaDispositivi();
            await caricaStato();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nel salvataggio'));
        }
    };

    /* Le due soglie dell'orario. Sono un accordo fra la clinica e chi ci
       lavora, non una costante del programma: cambiano con un contratto,
       non con un rilascio. Per questo si modificano da qui e stanno nei
       parametri. */
    const [formSoglie, setFormSoglie] = useState(null);

    const salvaSoglie = async () => {
        try {
            setErrore(null);
            setInCorso(true);
            await presenzeAPI.salvaImpostazioni({
                minuti_attesi: parseInt(formSoglie.minuti_attesi, 10),
                tolleranza_minuti: parseInt(formSoglie.tolleranza_minuti, 10),
            });
            setAvviso('Orario atteso aggiornato. Le giornate si rileggono subito.');
            setFormSoglie(null);
            await Promise.all([caricaDispositivi(), caricaGiornate()]);
        } catch (e) {
            setErrore(messaggio(e, 'Errore nel salvataggio'));
        } finally {
            setInCorso(false);
        }
    };

    const abilitaTimbratura = async (d, valore) => {
        /* Chiedere conferma solo quando si ACCENDE: abilitare il tablet
           sbagliato - quello della sala d'attesa - mette la timbratura in
           mano ai pazienti. Spegnerlo non fa danni. */
        if (valore && !window.confirm(
            `Abilitare "${d.nome}" alla timbratura?\n\n`
            + 'Da questo dispositivo si potrà timbrare con il PIN. '
            + 'Non abilitare il tablet della sala d\'attesa: lo tocca il paziente.')) return;
        try {
            setErrore(null);
            const r = await personaleAPI.abilitaTimbratura(d.id, valore);
            setAvviso(r.data.message);
            await caricaDispositivi();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nell\'aggiornamento'));
        }
    };

    const generaPin = async (d) => {
        if (!window.confirm(`Generare un PIN nuovo per ${d.nominativo}? `
            + 'Quello attuale, se c\'è, smette di funzionare.')) return;
        try {
            setErrore(null);
            const r = await personaleAPI.generaPin(d.id);
            setPinGenerato({ nominativo: d.nominativo, pin: r.data.data.pin });
            const e = await personaleAPI.dipendenti();
            setDipendenti(e.data.data.dipendenti || []);
        } catch (e) {
            setErrore(messaggio(e, 'Errore nella generazione del PIN'));
        }
    };

    /* ---------- documenti ---------- */

    const apri = async (d) => {
        try {
            setErrore(null);
            const r = await personaleAPI.apriDocumento(d.id);
            window.open(r.data.data.url, '_blank', 'noopener,noreferrer');
            await caricaDocumenti();
        } catch (e) {
            setErrore(messaggio(e, 'Impossibile aprire il documento'));
        }
    };

    const salvaDoc = async () => {
        try {
            setErrore(null);
            setInCorso(true);
            const fd = new FormData();
            fd.append('file', formDoc.file);
            ['dipendente_id', 'tipo', 'anno', 'mese', 'titolo', 'note'].forEach((k) => {
                if (formDoc[k]) fd.append(k, formDoc[k]);
            });
            const r = await personaleAPI.caricaDocumento(fd);
            setAvviso(r.data.message);
            setFormDoc(null);
            setPannello(null);
            await caricaDocumenti();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nel caricamento'));
        } finally {
            setInCorso(false);
        }
    };

    const eliminaDoc = async (d) => {
        if (!window.confirm(`Rimuovere "${d.titolo || nomeTipo(d.tipo)}" di ${d.nominativo}?`)) return;
        try {
            await personaleAPI.eliminaDocumento(d.id);
            setAvviso('Documento rimosso.');
            await caricaDocumenti();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nella rimozione'));
        }
    };

    const vediAccessi = async (d) => {
        try {
            const r = await personaleAPI.accessi(d.id);
            setAccessi({ documento: d, righe: r.data.data.accessi || [] });
        } catch (e) {
            setErrore(messaggio(e, 'Impossibile leggere il registro'));
        }
    };

    /* ---------- assenze ---------- */

    const salvaAssenza = async () => {
        try {
            setErrore(null);
            setInCorso(true);
            await presenzeAPI.creaAssenza(formAssenza);
            setAvviso('Assenza registrata.');
            setFormAssenza(null);
            setPannello(null);
            const r = await presenzeAPI.assenze(periodo.dal, periodo.al);
            setAssenze(r.data.data.assenze || []);
            await caricaGiornate();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nel salvataggio'));
        } finally {
            setInCorso(false);
        }
    };

    const eliminaAssenza = async (a) => {
        if (!window.confirm(`Togliere ${a.sigla} dal ${dataIta(a.dal)} al ${dataIta(a.al)} di ${a.nominativo}?`)) return;
        try {
            await presenzeAPI.eliminaAssenza(a.id);
            setAvviso('Assenza rimossa.');
            const r = await presenzeAPI.assenze(periodo.dal, periodo.al);
            setAssenze(r.data.data.assenze || []);
            await caricaGiornate();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nella rimozione'));
        }
    };

    /* ---------- organico ---------- */

    const apriFormDip = async (d) => {
        if (!d) {
            try {
                const r = await personaleAPI.candidati();
                setCandidati(r.data.data.candidati || []);
            } catch (e) { setCandidati([]); }
        }
        setFormDip(d ? { ...d } : { utente_id: '', assunto_il: '', matricola: '', ore_settimanali: '',
                                    minuti_standard: '' });
        setPannello('dipendente');
    };

    const salvaDip = async () => {
        try {
            setErrore(null);
            setInCorso(true);
            if (formDip.id) await personaleAPI.aggiornaDipendente(formDip.id, formDip);
            else await personaleAPI.creaDipendente(formDip);
            setAvviso(formDip.id ? 'Dipendente aggiornato.' : 'Dipendente aggiunto all\'organico.');
            setFormDip(null);
            setPannello(null);
            const r = await personaleAPI.dipendenti();
            setDipendenti(r.data.data.dipendenti || []);
            await caricaGiornate();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nel salvataggio'));
        } finally {
            setInCorso(false);
        }
    };

    /* ---------- interventi sul registro ---------- */

    const salvaTimbratura = async () => {
        try {
            setErrore(null);
            setInCorso(true);
            const f = formTimbratura;
            /* Un mese chiuso risponde 409 con codice `mese_chiuso`. Non e'
               un errore da mostrare e basta: si chiede conferma e si
               rimanda la stessa cosa con `forza`. La pausa fra il click e
               la scrittura e' tutta la protezione che serve - chi puo'
               correggere e' gia' solo l'amministrazione. */
            const manda = async (forza) => {
                const con = forza ? { ...f, forza: true } : f;
                if (f.azione === 'inserimento') return presenzeAPI.inserisciTimbratura(con);
                if (f.azione === 'correzione') return presenzeAPI.correggiTimbratura(f.id, con);
                return presenzeAPI.annullaTimbratura(f.id, f.motivo, forza || undefined);
            };
            try {
                await manda(false);
            } catch (e1) {
                if (e1?.response?.data?.codice !== 'mese_chiuso') throw e1;
                if (!window.confirm(`${e1.response.data.message}\n\n`
                    + 'L\'intervento resta nel registro con il suo motivo, come tutti gli altri.')) {
                    setInCorso(false);
                    return;
                }
                await manda(true);
            }
            setAvviso('Intervento registrato, con la sua traccia.');
            setFormTimbratura(null);
            await caricaGiornate();
            if (giornataAperta) {
                await leggiGiornata(giornataAperta);
                setPannello('giornata');
            } else {
                setPannello(null);
            }
        } catch (e) {
            setErrore(messaggio(e, 'Errore nell\'intervento'));
        } finally {
            setInCorso(false);
        }
    };

    /* ---------- il dettaglio della giornata ---------- */

    const leggiGiornata = async (g) => {
        setGiornataAperta({ ...g, dati: null, errore: null });
        try {
            const r = await presenzeAPI.giornata(g.dipendente_id, g.giorno);
            setGiornataAperta({ ...g, dati: r.data.data, errore: null });
        } catch (e) {
            setGiornataAperta({ ...g, dati: null,
                                errore: messaggio(e, 'Impossibile caricare la giornata') });
        }
    };

    const apriGiornata = (d, giorno) => {
        setPannello('giornata');
        leggiGiornata({ dipendente_id: d.id, nominativo: d.nominativo, giorno });
    };

    const chiudiGiornata = () => {
        setGiornataAperta(null);
        setPannello(null);
    };

    /* Dal dettaglio al modulo dell'intervento: quello di sempre. */
    const intervieni = (azione, t) => {
        const g = giornataAperta;
        setFormTimbratura({
            azione,
            id: t ? t.id : undefined,
            dipendente_id: g.dipendente_id,
            nominativo: g.nominativo,
            giorno: g.giorno,
            momento: t ? perCampoItaliano(t.momento) : `${g.giorno}T17:30`,
            tipo: t ? t.tipo : 'uscita',
            vecchia: t ? `${t.tipo} delle ${oraItaliana(t.momento)}` : null,
            motivo: '',
        });
        setPannello('timbratura');
    };

    const apriStorico = async () => {
        try {
            const [i, f] = await Promise.all([
                presenzeAPI.interventi(), presenzeAPI.rifiuti(),
            ]);
            setInterventi(i.data.data.interventi || []);
            setRifiuti(f.data.data.rifiuti || []);
            setVista('storico');
        } catch (e) {
            setErrore(messaggio(e, 'Impossibile caricare lo storico'));
        }
    };

    /* ---------- ferie e permessi: chiedere e decidere ---------- */

    const inviaRichiesta = async () => {
        try {
            setErrore(null);
            setInCorso(true);
            await presenzeAPI.richiediAssenza(formRichiesta);
            setAvviso('Richiesta inviata. Resta in attesa finché l\'amministrazione non decide.');
            setFormRichiesta(null);
            setPannello(null);
            await caricaAssenze();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nell\'invio della richiesta'));
        } finally {
            setInCorso(false);
        }
    };

    const ritiraRichiesta = async (a) => {
        if (!window.confirm(`Ritirare la richiesta dal ${dataIta(a.dal)} al ${dataIta(a.al)}?`)) return;
        try {
            setErrore(null);
            await presenzeAPI.annullaRichiesta(a.id);
            setAvviso('Richiesta ritirata.');
            await caricaAssenze();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nel ritiro'));
        }
    };

    const decidi = async () => {
        try {
            setErrore(null);
            setInCorso(true);
            const f = formDecisione;
            await presenzeAPI.decidiAssenza(f.id, f.stato, f.motivo_rifiuto);
            setAvviso(f.stato === 'approvata' ? 'Richiesta approvata.' : 'Richiesta respinta.');
            setFormDecisione(null);
            setPannello(null);
            await Promise.all([caricaAssenze(), caricaPendenti(), caricaGiornate()]);
        } catch (e) {
            setErrore(messaggio(e, 'Errore nella decisione'));
        } finally {
            setInCorso(false);
        }
    };

    /* ---------- straordinari ---------- */

    const autorizzaStraordinario = async () => {
        try {
            setErrore(null);
            setInCorso(true);
            const f = formStraordinario;
            /* `nominativo` e `copre` servono solo a disegnare il pannello:
               al server va quello che il server usa. Mandare campi che
               nessuno legge e' il modo in cui un domani qualcuno li legge
               per sbaglio. */
            await presenzeAPI.autorizzaStraordinario({
                dipendente_id: f.dipendente_id,
                dal: f.dal,
                al: f.al,
                minuti: parseInt(f.minuti, 10),
                motivo: f.motivo,
            });
            setAvviso('Straordinario autorizzato.');
            setFormStraordinario(null);
            setPannello(null);
            await Promise.all([caricaStraordinari(), caricaGiornate()]);
        } catch (e) {
            setErrore(messaggio(e, 'Errore nell\'autorizzazione'));
        } finally {
            setInCorso(false);
        }
    };

    const revocaStraordinario = async (x) => {
        if (!window.confirm(
            `Revocare i ${durata(x.minuti)} autorizzati a ${x.nominativo}?\n\n`
            + 'Le ore già lavorate tornano «da autorizzare»: è esattamente ciò che '
            + 'una revoca significa.')) return;
        try {
            setErrore(null);
            await presenzeAPI.revocaStraordinario(x.id);
            setAvviso('Autorizzazione revocata.');
            await Promise.all([caricaStraordinari(), caricaGiornate()]);
        } catch (e) {
            setErrore(messaggio(e, 'Errore nella revoca'));
        }
    };

    /* ---------- chiusura del mese ---------- */

    const caricaChiusure = async () => {
        try {
            const r = await presenzeAPI.chiusure();
            setChiusure(r.data.data.chiusure || []);
            setVista('chiusure');
        } catch (e) {
            setErrore(messaggio(e, 'Impossibile caricare le chiusure'));
        }
    };

    const guardaMese = async (mese) => {
        try {
            setErrore(null);
            setAnteprima(null);
            const r = await presenzeAPI.anteprimaChiusura(mese);
            setAnteprima(r.data.data);
        } catch (e) {
            setErrore(messaggio(e, 'Impossibile controllare il mese'));
        }
    };

    const chiudiMese = async () => {
        /* La conferma elenca cio' che resta in sospeso, e il mese si
           chiude lo stesso: un mese che si rifiuta di chiudersi finche'
           non torna tutto blocca a gennaio lo stipendio di venti persone
           per una timbratura dimenticata a ottobre. Cio' che resta si
           scrive nella riga di chiusura. */
        const a = anteprima;
        const resti = a ? (a.incomplete.length + a.da_autorizzare.length
                           + a.richieste_pendenti.length) : 0;
        const testo = resti
            ? `Restano ${a.incomplete.length} giornate incomplete, `
              + `${a.da_autorizzare.length} eccedenze non autorizzate e `
              + `${a.richieste_pendenti.length} richieste in attesa.\n\n`
              + 'Il mese si chiude lo stesso e queste cose vengono registrate '
              + 'nella chiusura. Procedere?'
            : `Chiudere ${meseDaChiudere}? Dopo la chiusura le timbrature di quel `
              + 'mese chiedono una conferma in più per essere corrette.';
        if (!window.confirm(testo)) return;
        try {
            setErrore(null);
            setInCorso(true);
            const r = await presenzeAPI.chiudiMese(meseDaChiudere);
            setAvviso(r.data.message);
            setAnteprima(null);
            await caricaChiusure();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nella chiusura'));
        } finally {
            setInCorso(false);
        }
    };

    const riapriMese = async (c) => {
        const mese = String(c.mese).slice(0, 7);
        const motivo = window.prompt(
            `Riaprire ${mese}?\n\n`
            + 'Il motivo è obbligatorio: se il mese è già stato mandato allo studio '
            + 'paghe, qualcosa di già pagato sta per cambiare.');
        if (motivo === null) return;
        try {
            setErrore(null);
            await presenzeAPI.riapriMese(mese, motivo);
            setAvviso('Mese riaperto.');
            await caricaChiusure();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nella riapertura'));
        }
    };

    /* ---------- export ---------- */

    const esporta = async (formato = 'xlsx') => {
        try {
            setErrore(null);
            setInCorso(true);
            const c = await presenzeAPI.controllo(periodo.dal, periodo.al);
            const d = c.data.data;
            if (d.incomplete.length || d.senza_timbrature.length) {
                const testo = [
                    d.incomplete.length
                        ? `${d.incomplete.length} giornate incomplete (manca una timbratura):\n`
                          + d.incomplete.slice(0, 8).map((x) => `  ${x.dipendente} — ${dataIta(x.giorno)}`).join('\n')
                          + (d.incomplete.length > 8 ? `\n  e altre ${d.incomplete.length - 8}` : '')
                        : '',
                    d.senza_timbrature.length
                        ? `Nessuna timbratura nel periodo per: ${d.senza_timbrature.join(', ')}`
                        : '',
                ].filter(Boolean).join('\n\n');
                if (!window.confirm(`${testo}\n\nGenerare il file comunque?`)) {
                    setInCorso(false);
                    return;
                }
            }

            const r = await presenzeAPI.esporta(periodo.dal, periodo.al, undefined, formato);
            /* Il file arriva come blob e si consegna al browser: non c'e' un
               indirizzo pubblico da aprire, e non deve esserci. */
            const url = window.URL.createObjectURL(new Blob([r.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `presenze_${periodo.dal}_${periodo.al}.${formato}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            setAvviso('File generato.');
        } catch (e) {
            setErrore(messaggio(e, 'Errore nella generazione del file'));
        } finally {
            setInCorso(false);
        }
    };

    /* ---------- viste ---------- */

    /* L'amministratore che e' anche dipendente ha anche «Le mie ore»: e'
       li' che chiede la correzione delle SUE timbrature, che approva un
       altro amministratore (Alex, 22/10/2026, conferma 5). */
    /* «Le mie buste paga» accanto a «Le mie ore», per chi e' in organico;
       «Buste paga» solo a chi ha il permesso dedicato (Alex, 26/10/2026):
       lo dice il server, e col modulo spento nessuna delle due. */
    const mieBuste = statoBuste?.attivo && statoBuste?.in_organico
        ? [['mie_buste', 'Le mie buste paga']] : [];
    const gestioneBuste = statoBuste?.attivo && statoBuste?.gestisce
        ? [['buste', 'Buste paga']] : [];
    const schede = eAdmin
        ? [['presenze', 'Presenze'],
           ...(stato?.in_organico ? [['mie', 'Le mie ore'], ['timbrature', 'Le mie timbrature']] : []),
           ...mieBuste,
           ['assenze', 'Ferie e permessi'],
           ['straordinari', 'Straordinari'], ['chiusure', 'Chiusura mese'],
           ...gestioneBuste,
           ['documenti', 'Documenti'], ['organico', 'Organico'],
           ['tablet', 'Tablet'], ['storico', 'Registro interventi']]
        : [['mie', 'Le mie ore'],
           ...(stato?.in_organico ? [['timbrature', 'Le mie timbrature']] : []),
           ...mieBuste, ...gestioneBuste, ['ferie', 'Le mie ferie'],
           ['documenti', 'I miei documenti']];
    /* Col modulo acceso buste paga, CU e contratti stanno nelle loro
       linguette; spento, la dipendente li ritrova qui come prima. */
    const documentiMostrati = statoBuste?.attivo
        ? documenti.filter((d) => !TIPI_RISERVATI.includes(d.tipo)) : documenti;

    /* Chi non e' amministrazione riceve dal server solo le proprie giornate;
       l'amministrazione quelle di tutti, e le sue si ritrovano col proprio
       numero in organico (lo manda /stato). */
    const mieGiornate = eAdmin
        ? giornate.find((d) => d.id === stato?.dipendente_id)
        : giornate[0];
    /* I giorni in cui si puo' chiedere una correzione, per proporre il
       pulsante sulla riga: la regola vera la applica il server. */
    const finestraCorr = finestraRichieste(mieCorr.entro_giorni);

    /* Quanti minuti al giorno basterebbero a coprire TUTTE le eccedenze
       non autorizzate di una persona nel periodo mostrato.
       E' il massimo, non la somma e non la media: l'autorizzazione vale
       «fino a N minuti al giorno», quindi deve reggere la giornata
       peggiore. Con la somma si autorizzerebbe molto piu' del dovuto,
       con la media resterebbe scoperta meta' delle giornate - e il
       compito non si svuoterebbe mai, che e' il motivo per cui questo
       pulsante esiste. */
    const copertura = (d) => {
        const giorni = (d.giornate || []).filter((g) => g.da_autorizzare > 0);
        return {
            giornate: giorni.length,
            minuti: giorni.reduce((max, g) => Math.max(max, g.da_autorizzare), 0),
            totale: giorni.reduce((t, g) => t + g.da_autorizzare, 0),
        };
    };

    /* Quando si arriva da un compito si guarda una persona sola. Il
       filtro e' qui e non nella chiamata al server: le ore le ha gia'
       tutte, e rifarle chiedere cambierebbe i totali del riquadro in
       cima senza che nessuno l'abbia chiesto. */
    const giornateMostrate = soloDipendente
        ? giornate.filter((d) => d.id === soloDipendente)
        : giornate;

    const totaleIncomplete = useMemo(
        () => giornate.reduce((t, d) => t + (d.incomplete || 0), 0), [giornate]
    );

    return (
        <div className="adp-wrap">
            <IntestazioneSezione
                titolo="Area dipendenti"
                icona={<Clock size={20} aria-hidden="true" />}
                viste={schede.map(([chiave, etichetta]) => ({
                    chiave, etichetta,
                    /* Le giornate incomplete si contano solo sulla
                       linguetta delle presenze, e solo se ce ne sono.
                       Le richieste in attesa fanno lo stesso su ferie e
                       permessi: senza un numero, una richiesta puo'
                       restare ferma per settimane perche' nessuno ha
                       motivo di aprire quella schermata. */
                    contatore: chiave === 'presenze' && totaleIncomplete > 0
                        ? totaleIncomplete
                        : chiave === 'assenze' && pendenti.quante > 0
                            ? pendenti.quante : undefined,
                }))}
                vista={vista}
                onVista={(v) => {
                    /* Tre linguette fanno qualcosa in piu' del cambio
                       vista: il registro interventi e le chiusure se le
                       aprono da se', e il tablet ha bisogno dell'elenco
                       dispositivi prima di disegnarsi. */
                    if (v === 'storico') return apriStorico();
                    if (v === 'chiusure') return caricaChiusure();
                    if (v === 'tablet') caricaDispositivi();
                    return setVista(v);
                }}
                /* Si timbra dal TABLET, non da qui: la timbratura vuole la
                   chiave del dispositivo oltre al PIN, e un pulsante dietro
                   il solo login permetterebbe di timbrare da casa. Qui si
                   mostra lo stato e si gestisce il proprio PIN. */
                azione={stato?.in_organico && (
                    <div className="adp-timbra">
                        <span className="adp-ultima">
                            {stato.ultima
                                ? `Ultima timbratura: ${stato.ultima.tipo} alle ${oraIta(stato.ultima.momento)}`
                                : 'Nessuna timbratura oggi'}
                            {stato.giornata && parseFloat(stato.giornata.ore) > 0
                                && ` · ${ore(stato.giornata.ore)} h`}
                        </span>
                        {!stato.attiva && (
                            <span className="adp-spenta">
                                <ShieldAlert size={15} /> Rilevazione presenze non attiva
                            </span>
                        )}
                        <button className={`adp-btn ${statoPin && !statoPin.impostato ? 'adp-btn--principale' : ''}`}
                                onClick={() => {
                                    setFormPin({ attuale: '', nuovo: '', conferma: '' });
                                    setPannello('pin');
                                }}>
                            <KeyRound size={15} />
                            {statoPin && !statoPin.impostato ? 'Imposta il PIN'
                             : statoPin?.pin_da_cambiare ? 'Cambia il PIN (consigliato)'
                             : 'Cambia il PIN'}
                        </button>
                    </div>
                )}
            />

            <ErroreLettura classe="adp-errore" Contenitore="div"
                testo={erroriLettura.giornate || erroriLettura.documenti || null}
                onRiprova={() => {
                    if (erroriLettura.giornate) caricaGiornate();
                    if (erroriLettura.documenti) caricaDocumenti();
                    if (!stato) caricaStato();
                }} />
            {errore && (
                <div className="adp-errore">
                    <AlertTriangle size={15} /> {errore}
                    <button onClick={() => setErrore(null)}><X size={14} /></button>
                </div>
            )}
            {avviso && (
                <div className="adp-avviso">
                    <Check size={15} /> {avviso}
                    <button onClick={() => setAvviso(null)}><X size={14} /></button>
                </div>
            )}

            {/* ---- periodo, dove serve ---- */}
            {['presenze', 'assenze', 'mie', 'ferie', 'straordinari'].includes(vista) && (
                <div className="adp-periodo">
                    <label>
                        <span>Dal</span>
                        <input type="date" value={periodo.dal}
                               onChange={(e) => setPeriodo({ ...periodo, dal: e.target.value })} />
                    </label>
                    <label>
                        <span>Al</span>
                        <input type="date" value={periodo.al}
                               onChange={(e) => setPeriodo({ ...periodo, al: e.target.value })} />
                    </label>
                    {eAdmin && vista === 'presenze' && (
                        <>
                            <button className="adp-btn adp-btn--principale"
                                    onClick={() => esporta('xlsx')} disabled={inCorso}>
                                <Download size={15} /> File per lo studio paghe
                            </button>
                            {/* Il CSV e' un elenco, non una matrice: serve a chi
                                deve importarlo o farci un conto, non a chi lo
                                stampa. Sta accanto e non al posto dell'altro. */}
                            <button className="adp-btn" onClick={() => esporta('csv')}
                                    disabled={inCorso}
                                    title="Una riga per persona per giorno: per importarlo in un altro gestionale">
                                <Download size={15} /> CSV
                            </button>
                        </>
                    )}
                </div>
            )}

            {/* ============ PRESENZE (admin) ============ */}
            {vista === 'presenze' && avvisoPresenze && (
                <p className="adp-avviso-righe">{avvisoPresenze}</p>
            )}

            {/* Le richieste di correzione delle dipendenti (339): sopra la
                griglia, perche' una richiesta in attesa e' una cosa da fare. */}
            {vista === 'presenze' && eAdmin && (
                <RichiesteCorrezione inEvidenza={evidenzaCorr}
                                     onDecisa={caricaGiornate}
                                     onAvviso={setAvviso} onErrore={setErrore} />
            )}

            {vista === 'presenze' && soloDipendente && (
                <p className="adp-filtro-attivo">
                    Stai guardando una persona sola, come indicato dal compito.
                    <button className="adp-btn" onClick={() => setSoloDipendente(null)}>
                        <X size={14} /> Mostra tutti
                    </button>
                </p>
            )}

            {vista === 'presenze' && (
                giornateMostrate.length === 0 ? (
                    <p className="adp-vuoto">
                        Nessun dipendente in forza nel periodo. Si aggiungono dalla
                        scheda Organico.
                    </p>
                ) : giornateMostrate.map((d) => (
                    <div key={d.id} className="adp-blocco" data-dipendente={d.id}>
                        <div className="adp-blocco-testa">
                            <h3 className={d.non_risolto ? 'adp-non-risolto' : undefined}>
                                {d.nominativo}
                            </h3>
                            <span>
                                {ore(d.ore_totali)} ore nel periodo
                                {/* Le ore ATTESE si sommano solo sulle giornate
                                    confrontabili - lavorate o coperte da
                                    un'assenza - e non su tutti i giorni del
                                    calendario: domeniche comprese, il confronto
                                    racconterebbe un ammanco che non esiste. */}
                                {d.conteggi?.giorni_confrontabili > 0 && (
                                    <> su {ore(Math.round((d.conteggi.minuti_attesi / 60) * 100) / 100)}
                                       {' '}attese in {d.conteggi.giorni_confrontabili} giornate</>
                                )}
                                {/* Festivi e domeniche lavorati a parte, non
                                    nello straordinario (Alex, 6/10/2026). */}
                                {d.conteggi?.minuti_festivi_lavorati > 0 && (
                                    <>{' '}· {durata(d.conteggi.minuti_festivi_lavorati)} in
                                       {' '}{d.conteggi.festivi_lavorati} festivi lavorati</>
                                )}
                                {d.conteggi?.minuti_domeniche_lavorate > 0 && (
                                    <>{' '}· {durata(d.conteggi.minuti_domeniche_lavorate)} in
                                       {' '}{d.conteggi.domeniche_lavorate} domeniche lavorate</>
                                )}
                                {d.conteggi?.straordinario_da_autorizzare > 0 && (
                                    <b className="adp-attesa">
                                        {' '}· {durata(d.conteggi.straordinario_da_autorizzare)} da autorizzare
                                    </b>
                                )}
                                {d.incomplete > 0 && (
                                    <b className="adp-critico"> · {d.incomplete} giornate incomplete</b>
                                )}
                            </span>
                            {/* IL GESTO NORMALE E' IL PERIODO, non la giornata.
                                Il pulsante propone gia' il periodo mostrato e i
                                minuti che bastano a coprire tutte le eccedenze:
                                un clic invece di ventisette. Un flusso che ne
                                chiede ventisette non verra' usato, le ore
                                resterebbero non autorizzate lo stesso, e il
                                compito notturno non si svuoterebbe mai.
                                L'autorizzazione a giornata singola resta, ed e'
                                il pulsante accanto: serve ai casi eccezionali. */}
                            {(() => {
                                const c = copertura(d);
                                if (!c.giornate) {
                                    return (
                                        <button className="adp-icona" title="Autorizza straordinario"
                                            onClick={() => {
                                                setFormStraordinario({
                                                    dipendente_id: d.id, nominativo: d.nominativo,
                                                    dal: periodo.dal, al: periodo.al,
                                                    minuti: '', motivo: '',
                                                });
                                                setPannello('straordinario');
                                            }}>
                                            <Timer size={15} />
                                        </button>
                                    );
                                }
                                return (
                                    <button className="adp-btn adp-btn--piccolo"
                                        title={`Concede fino a ${durata(c.minuti)} al giorno `
                                             + `dal ${dataIta(periodo.dal)} al ${dataIta(periodo.al)}: `
                                             + `copre tutte le ${c.giornate} giornate scoperte`}
                                        onClick={() => {
                                            setFormStraordinario({
                                                dipendente_id: d.id, nominativo: d.nominativo,
                                                dal: periodo.dal, al: periodo.al,
                                                minuti: String(c.minuti), motivo: '',
                                                copre: c,
                                            });
                                            setPannello('straordinario');
                                        }}>
                                        <Timer size={15} /> Autorizza tutto il periodo
                                    </button>
                                );
                            })()}
                        </div>
                        {/* Le ordinarie e lo straordinario del periodo (Alex,
                            2/11/2026): quattro conteggi diversi, li calcola il
                            server (orarioLavoro). In busta va lo straordinario
                            autorizzato; le mancanti non si compensano. */}
                        {d.conteggi && (
                            <p className="adp-nota" data-quattro-voci={d.id}>
                                Ordinarie {minutiVoce(d.conteggi.minuti_ordinari)}
                                {' '}· straordinario autorizzato {minutiVoce(d.conteggi.minuti_straordinario_autorizzato)}
                                {' '}· straordinario da autorizzare {minutiVoce(d.conteggi.minuti_straordinario_da_autorizzare)}
                                {' '}· ore mancanti {minutiVoce(d.conteggi.minuti_mancanti)}
                                {d.conteggi.minuti_standard != null && (
                                    <>{' '}(standard {minutiVoce(d.conteggi.minuti_standard)} al giorno)</>
                                )}
                            </p>
                        )}
                        <div className="adp-griglia">
                            {d.giornate.map((g) => {
                                const chiave = String(g.giorno).slice(0, 10);
                                const rotta = g.spaiate > 0;
                                const st = STATI[g.stato] || {};
                                /* Una timbratura dimenticata inserita dalla
                                   dipendente dal tablet, e «da controllare»
                                   se la sequenza non torna: lo decide il
                                   server (segni), qui si disegna. */
                                const segno = d.segni?.[chiave];
                                /* Il giorno «corretto» (339): chi ha chiesto,
                                   chi ha approvato, quando. Lo decide il server
                                   (giornate_corrette), qui si disegna. */
                                const corr = d.corretti?.[chiave];
                                /* Il titolo dice il FATTO: quante ore, quante
                                   attese, cosa manca. Non «ritardo», non
                                   «ammanco»: chi legge decide se e' un
                                   problema, e per farlo gli serve il dato. */
                                const spiega = [
                                    segno?.da_controllare ? 'Da controllare' : null,
                                    /* Il festivo dice quale festa; festivi e
                                       domeniche lavorati si contano a parte
                                       (Alex, 6/10/2026). */
                                    g.festa ? `${st.t}: ${g.festa}` : st.t,
                                    g.minuti ? `${durata(g.minuti)} lavorate` : null,
                                    g.attesi ? `${durata(g.attesi)} attese` : null,
                                    g.da_autorizzare ? `${durata(g.da_autorizzare)} da autorizzare` : null,
                                    g.autorizzati ? `${durata(g.autorizzati)} autorizzati` : null,
                                    segno ? (segno.dichiarate === 1
                                        ? '1 timbratura inserita a mano dalla dipendente'
                                        : `${segno.dichiarate} timbrature inserite a mano dalla dipendente`) : null,
                                    corr ? testoCorretta(corr) : null,
                                ].filter(Boolean).join(' · ');
                                return (
                                    <button key={chiave} data-giorno={chiave}
                                        className={`adp-giorno ${g.domenica ? 'domenica' : ''}`
                                            + `${g.festivo ? ' adp-festivo' : ''}`
                                            + `${rotta ? ' rotta' : ''}${g.sigla ? ' assente' : ''}`
                                            + ` stato-${g.stato || 'nessuno'}`
                                            + `${segno ? ' a-mano' : ''}${segno?.da_controllare ? ' da-controllare' : ''}`
                                            + `${corr ? ' corretto' : ''}`}
                                        title={`${spiega}\nClicca per il dettaglio della giornata`}
                                        onClick={() => apriGiornata(d, chiave)}>
                                        <span className="adp-gg">{chiave.slice(8)}</span>
                                        <span className="adp-ore">
                                            {g.sigla && g.sostituisce ? g.sigla : ore(g.ore)}
                                        </span>
                                        {g.sigla && !g.sostituisce && (
                                            <span className="adp-sigla">{g.sigla}</span>
                                        )}
                                        {rotta && <span className="adp-spia" />}
                                        {segno && (
                                            <span className="adp-a-mano" aria-label="Timbratura inserita a mano dalla dipendente">✎</span>
                                        )}
                                        {g.da_autorizzare > 0 && (
                                            <span className="adp-piu">+{oreEMinuti(g.da_autorizzare)}</span>
                                        )}
                                        {corr && (
                                            <span className="adp-corretto" aria-label="Giornata corretta">C</span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))
            )}

            {/* ============ LE MIE ORE ============ */}
            {vista === 'mie' && (
                !mieGiornate ? (
                    <p className="adp-vuoto">
                        Il tuo account non è in organico: non ci sono ore da mostrare.
                    </p>
                ) : (
                    <>
                        <div className="adp-riepilogo">
                            <div><b>{ore(mieGiornate.ore_totali)}</b><span>ore nel periodo</span></div>
                            <div><b>{mieGiornate.giornate.filter((g) => g.completa).length}</b>
                                 <span>giornate complete</span></div>
                            <div className={mieGiornate.incomplete ? 'critico' : ''}>
                                <b>{mieGiornate.incomplete}</b><span>da sistemare</span></div>
                        </div>
                        <table className="adp-tabella">
                            <thead>
                                <tr>
                                    <th>Giorno</th><th className="num">Ore</th>
                                    <th>Causale</th><th>Stato</th><th />
                                </tr>
                            </thead>
                            <tbody>
                                {mieGiornate.giornate
                                    .filter((g) => g.timbrature > 0 || g.sigla)
                                    .map((g) => {
                                    const chiave = String(g.giorno).slice(0, 10);
                                    const corr = mieGiornate.corretti?.[chiave];
                                    const siChiede = finestraCorr && chiave >= finestraCorr.dal
                                        && chiave <= finestraCorr.al;
                                    return (
                                    <tr key={String(g.giorno)} data-giorno={chiave}
                                        className={`${g.domenica ? 'domenica' : ''}${g.festivo ? ' adp-festivo' : ''}`}>
                                        <td>{dataIta(g.giorno)}{g.festa ? ` · ${g.festa}` : ''}</td>
                                        <td className="num">{ore(g.ore)}</td>
                                        <td>{g.sigla ? `${g.sigla}${g.ore_causale ? ` (${ore(g.ore_causale)} h)` : ''}` : '—'}</td>
                                        <td>
                                            {g.spaiate > 0 ? (
                                                <span className="adp-pillola guasto">
                                                    manca una timbratura
                                                </span>
                                            ) : g.completa ? (
                                                <span className="adp-pillola buono">completa</span>
                                            ) : '—'}
                                            {corr && (
                                                <span className="adp-pillola correzione" title={testoCorretta(corr)}
                                                      data-corretta>
                                                    corretta
                                                </span>
                                            )}
                                        </td>
                                        <td className="num">
                                            {siChiede && (
                                                <button className="adp-btn adp-btn--piccolo"
                                                        onClick={() => setModuloCorr({ giorno: chiave })}
                                                        data-azione="chiedi-correzione-giorno">
                                                    Chiedi correzione
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        <p className="adp-nota">
                            Se una giornata è incompleta o un orario è sbagliato, chiedi la
                            correzione: l&apos;amministrazione la approva o la rifiuta con il
                            motivo. La timbratura di prima non si cancella, e la giornata
                            corretta resta segnata con chi l&apos;ha chiesta e chi l&apos;ha approvata.
                        </p>
                        <MieCorrezioni richieste={mieCorr.richieste} entroGiorni={mieCorr.entro_giorni}
                                       inEvidenza={evidenzaCorr}
                                       onChiedi={() => setModuloCorr({ giorno: '' })}
                                       onRitira={ritiraCorrezione} />
                    </>
                )
            )}

            {/* ============ LE MIE TIMBRATURE (chi e' in organico) ============
                Sola lettura (Alex, 28/10/2026): la correzione si chiede da
                «Le mie ore». Il periodo «Dal/Al» qui non vale: il mese e'
                il corrente o il precedente, e lo dice il server. */}
            {vista === 'timbrature' && (
                <MieTimbrature mese={meseTimbrature}
                               onMese={(m) => scriviIndirizzo('timbrature', m)} />
            )}

            {/* ============ LE MIE FERIE (dipendente) ============
                Il dipendente CHIEDE, non registra. Il pulsante dice
                «Chiedi» e non «Aggiungi» perche' e' esattamente la
                differenza: quello che scrive qui resta in attesa. */}
            {vista === 'ferie' && (
                <>
                    <div className="adp-azioni-riga">
                        <button className="adp-btn adp-btn--principale" onClick={() => {
                            setFormRichiesta({ causale_id: '', dal: '', al: '', ore: '', note: '' });
                            setPannello('richiesta');
                        }}>
                            <Send size={15} /> Chiedi ferie o permesso
                        </button>
                    </div>
                    {assenze.length === 0 ? (
                        <p className="adp-vuoto">
                            Nessuna richiesta nel periodo. Le ferie si chiedono da qui:
                            restano in attesa finché l&apos;amministrazione non risponde.
                        </p>
                    ) : (
                        <table className="adp-tabella">
                            <thead>
                                <tr>
                                    <th>Causale</th><th>Dal</th><th>Al</th>
                                    <th className="num">Ore</th><th>Stato</th><th>Note</th><th />
                                </tr>
                            </thead>
                            <tbody>
                                {assenze.map((a) => {
                                    const st = STATI_ASSENZA[a.stato] || {};
                                    return (
                                        <tr key={a.id}>
                                            <td><b>{a.sigla}</b> {a.descrizione}</td>
                                            <td>{dataIta(a.dal)}</td>
                                            <td>{dataIta(a.al)}</td>
                                            <td className="num">{a.ore ? ore(a.ore) : 'giornata'}</td>
                                            <td><span className={`adp-pillola ${st.c}`}>{st.t || a.stato}</span></td>
                                            <td className="adp-sub">
                                                {a.stato === 'respinta' ? a.motivo_rifiuto : (a.note || '—')}
                                            </td>
                                            <td className="num">
                                                {/* Si ritira solo cio' che e' ancora in
                                                    attesa: ferie gia' approvate sono un
                                                    accordo con la clinica, e disdirle
                                                    passa dall'amministrazione. */}
                                                {a.stato === 'richiesta' && (
                                                    <button className="adp-icona" title="Ritira la richiesta"
                                                            onClick={() => ritiraRichiesta(a)}>
                                                        <X size={15} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </>
            )}

            {/* ============ ASSENZE ============ */}
            {vista === 'assenze' && (
                <>
                    <div className="adp-azioni-riga">
                        <button className="adp-btn" onClick={() => {
                            setFormAssenza({ dipendente_id: '', causale_id: '', dal: '', al: '', ore: '', note: '' });
                            setPannello('assenza');
                        }}>
                            <Plus size={15} /> Registra ferie o permesso
                        </button>
                        {pendenti.quante > 0 && (
                            <span className="adp-nota-riga">
                                <Hourglass size={14} /> {pendenti.quante}{' '}
                                {pendenti.quante === 1 ? 'richiesta in attesa' : 'richieste in attesa'}
                                {/* Una richiesta per giorni gia' passati non si
                                    autorizza: si registra. Chi decide deve saperlo. */}
                                {pendenti.gia_passate > 0
                                    && `, di cui ${pendenti.gia_passate} per giorni già passati`}
                            </span>
                        )}
                    </div>
                    {/* Le richieste in attesa stanno in cima, non in mezzo
                        all'elenco ordinato per data: sono le uniche righe su
                        cui qualcuno deve fare qualcosa. */}
                    {pendenti.richieste.length > 0 && (
                        <div className="adp-pendenti">
                            <h4>Da decidere</h4>
                            <table className="adp-tabella">
                                <thead>
                                    <tr>
                                        <th>Dipendente</th><th>Causale</th><th>Dal</th><th>Al</th>
                                        <th className="num">Ore</th><th>Chiesta il</th><th>Note</th><th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {pendenti.richieste.map((r) => (
                                        <tr key={r.id} className={r.gia_passata ? 'adp-passata' : undefined}>
                                            <td>{r.nominativo}</td>
                                            <td><b>{r.sigla}</b> {r.descrizione}</td>
                                            <td>{dataIta(r.dal)}</td>
                                            <td>{dataIta(r.al)}</td>
                                            <td className="num">{r.ore ? ore(r.ore) : 'giornata'}</td>
                                            <td className="adp-sub">{dataIta(r.richiesta_il)}</td>
                                            <td className="adp-sub">{r.note || '—'}</td>
                                            <td className="num adp-decidi">
                                                <button className="adp-icona buono" title="Approva"
                                                    onClick={() => {
                                                        setFormDecisione({ ...r, stato: 'approvata', motivo_rifiuto: '' });
                                                        setPannello('decisione');
                                                    }}>
                                                    <ThumbsUp size={15} />
                                                </button>
                                                <button className="adp-icona guasto" title="Respingi"
                                                    onClick={() => {
                                                        setFormDecisione({ ...r, stato: 'respinta', motivo_rifiuto: '' });
                                                        setPannello('decisione');
                                                    }}>
                                                    <ThumbsDown size={15} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {assenze.length === 0 ? (
                        <p className="adp-vuoto">Nessuna assenza nel periodo.</p>
                    ) : (
                        <table className="adp-tabella">
                            <thead>
                                <tr>
                                    <th>Dipendente</th><th>Causale</th><th>Dal</th><th>Al</th>
                                    <th className="num">Ore</th><th>Stato</th><th>Note</th><th />
                                </tr>
                            </thead>
                            <tbody>
                                {assenze.map((a) => (
                                    <tr key={a.id}>
                                        <td>{a.nominativo}</td>
                                        <td><b>{a.sigla}</b> {a.descrizione}</td>
                                        <td>{dataIta(a.dal)}</td>
                                        <td>{dataIta(a.al)}</td>
                                        <td className="num">{a.ore ? ore(a.ore) : 'giornata'}</td>
                                        <td>
                                            <span className={`adp-pillola ${(STATI_ASSENZA[a.stato] || {}).c}`}>
                                                {(STATI_ASSENZA[a.stato] || {}).t || a.stato}
                                            </span>
                                        </td>
                                        <td className="adp-sub">{a.note || '—'}</td>
                                        <td className="num">
                                            {eAdmin && (
                                                <button className="adp-icona" onClick={() => eliminaAssenza(a)}>
                                                    <Trash2 size={15} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </>
            )}

            {/* ============ STRAORDINARI ============ */}
            {vista === 'straordinari' && (
                <>
                    <p className="adp-nota">
                        Lo straordinario si autorizza <b>prima</b>. Autorizzare a cose
                        fatte resta possibile — un imprevisto esiste — ma la data in
                        cui è stato concesso resta scritta e si vede.
                        Senza autorizzazione le ore oltre l&apos;orario <b>non spariscono</b>:
                        restano nelle ore vere e si marcano «da autorizzare».
                    </p>
                    <div className="adp-azioni-riga">
                        {eAdmin && (
                            <button className="adp-btn" onClick={() => {
                                setFormStraordinario({
                                    dipendente_id: '', dal: periodo.dal, al: periodo.dal,
                                    minuti: '', motivo: '',
                                });
                                setPannello('straordinario');
                            }}>
                                <Plus size={15} /> Autorizza straordinario
                            </button>
                        )}
                    </div>
                    {straordinari.length === 0 ? (
                        <p className="adp-vuoto">Nessuna autorizzazione nel periodo.</p>
                    ) : (
                        <table className="adp-tabella">
                            <thead>
                                <tr>
                                    <th>Dipendente</th><th>Dal</th><th>Al</th>
                                    <th className="num">Minuti</th><th>Motivo</th>
                                    <th>Concesso</th><th />
                                </tr>
                            </thead>
                            <tbody>
                                {straordinari.map((x) => (
                                    <tr key={x.id} className={x.revocato_il ? 'adp-revocato' : undefined}>
                                        <td>{x.nominativo}</td>
                                        <td>{dataIta(x.dal)}</td>
                                        <td>{dataIta(x.al)}</td>
                                        <td className="num">{durata(x.minuti)}</td>
                                        <td className="adp-sub">{x.motivo || '—'}</td>
                                        <td className="adp-sub">
                                            {dataIta(x.concesso_il)}
                                            {x.concesso_da_cognome && ` · ${x.concesso_da_cognome}`}
                                            {x.revocato_il && (
                                                <b> · revocata il {dataIta(x.revocato_il)}</b>
                                            )}
                                        </td>
                                        <td className="num">
                                            {eAdmin && !x.revocato_il && (
                                                <button className="adp-icona" title="Revoca"
                                                        onClick={() => revocaStraordinario(x)}>
                                                    <Trash2 size={15} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </>
            )}

            {/* ============ CHIUSURA DEL MESE ============ */}
            {vista === 'chiusure' && (
                <>
                    <p className="adp-nota">
                        Dopo la chiusura, correggere una timbratura di quel mese chiede
                        una conferma in più: il mese è già stato mandato allo studio
                        paghe. Non è vietato — un errore vero si corregge anche dopo —
                        ma non deve succedere per distrazione.
                    </p>
                    <div className="adp-azioni-riga">
                        <label>
                            <span>Mese</span>
                            <CampoMeseAnno value={testoMeseChiusura} aria-label="Mese da chiudere"
                                   onChange={(t) => {
                                       setTestoMeseChiusura(t);
                                       setAnteprima(null);
                                       if (aIso(t)) setMeseDaChiudere(aIso(t));
                                   }} />
                        </label>
                        <button className="adp-btn" onClick={() => guardaMese(meseDaChiudere)} disabled={!aIso(testoMeseChiusura)}>
                            <AlertTriangle size={15} /> Cosa resta in sospeso
                        </button>
                        <button className="adp-btn adp-btn--principale"
                                onClick={chiudiMese} disabled={inCorso || !aIso(testoMeseChiusura)}>
                            <Lock size={15} /> Chiudi {meseDaChiudere}
                        </button>
                    </div>

                    {anteprima && (
                        <div className="adp-anteprima">
                            <h4>In sospeso su {String(anteprima.mese).slice(0, 7)}</h4>
                            {!anteprima.incomplete.length && !anteprima.da_autorizzare.length
                             && !anteprima.richieste_pendenti.length && !anteprima.sotto.length ? (
                                <p className="adp-vuoto">Non è rimasto niente in sospeso.</p>
                            ) : (
                                <ul>
                                    {anteprima.incomplete.length > 0 && (
                                        <li><b>{anteprima.incomplete.length}</b> giornate senza una
                                            timbratura. Le ore di quei giorni non sono attendibili.</li>
                                    )}
                                    {anteprima.da_autorizzare.length > 0 && (
                                        <li><b>{anteprima.da_autorizzare.length}</b> giornate con ore
                                            oltre l&apos;orario che nessuno ha autorizzato.</li>
                                    )}
                                    {anteprima.richieste_pendenti.length > 0 && (
                                        <li><b>{anteprima.richieste_pendenti.length}</b> richieste di
                                            assenza ancora in attesa. Una richiesta pendente non è
                                            un&apos;assenza: nell&apos;export non esiste.</li>
                                    )}
                                    {anteprima.sotto.length > 0 && (
                                        <li><b>{anteprima.sotto.length}</b> giornate sotto l&apos;orario atteso.</li>
                                    )}
                                </ul>
                            )}
                        </div>
                    )}

                    {chiusure.length === 0 ? (
                        <p className="adp-vuoto">Nessun mese chiuso finora.</p>
                    ) : (
                        <table className="adp-tabella">
                            <thead>
                                <tr>
                                    <th>Mese</th><th>Chiuso il</th><th>Da</th>
                                    <th className="num">Incomplete</th><th className="num">Da autorizzare</th>
                                    <th>Stato</th><th />
                                </tr>
                            </thead>
                            <tbody>
                                {chiusure.map((c) => (
                                    <tr key={c.id}>
                                        <td><b>{String(c.mese).slice(0, 7)}</b></td>
                                        <td>{dataIta(c.chiuso_il)}</td>
                                        <td className="adp-sub">{c.chiuso_da_cognome || '—'}</td>
                                        <td className="num">{c.giornate_aperte}</td>
                                        <td className="num">{c.giornate_eccedenza}</td>
                                        <td>
                                            {c.riaperto_il ? (
                                                <span className="adp-pillola attesa" title={c.motivo_riapertura}>
                                                    riaperto il {dataIta(c.riaperto_il)}
                                                </span>
                                            ) : (
                                                <span className="adp-pillola buono">chiuso</span>
                                            )}
                                        </td>
                                        <td className="num">
                                            {!c.riaperto_il && (
                                                <button className="adp-icona" title="Riapri il mese"
                                                        onClick={() => riapriMese(c)}>
                                                    <Unlock size={15} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </>
            )}

            {/* ============ BUSTE PAGA (341) ============ */}
            {vista === 'mie_buste' && (
                <LeMieBustePaga evidenza={daCompito?.documentoPersonale} onErrore={errBuste} />
            )}
            {/* La stessa condizione della linguetta: chi arriva a «buste» dallo
               stato della navigazione senza il permesso non vede i pulsanti. */}
            {vista === 'buste' && statoBuste?.attivo && statoBuste?.gestisce && (
                <GestioneBustePaga onErrore={errBuste} onAvviso={avvisoBuste} />
            )}

            {/* ============ DOCUMENTI ============ */}
            {vista === 'documenti' && (
                <>
                    {eAdmin && (
                        <div className="adp-azioni-riga">
                            <button className="adp-btn" onClick={() => {
                                setFormDoc({ dipendente_id: '', tipo: 'comunicazione',
                                             anno: new Date().getFullYear(), mese: '', titolo: '', note: '', file: null });
                                setPannello('documento');
                            }}>
                                <Plus size={15} /> Carica un documento
                            </button>
                        </div>
                    )}
                    {documentiMostrati.length === 0 ? (
                        <p className="adp-vuoto">
                            {eAdmin ? 'Nessun documento caricato.'
                                    : 'Non ci sono documenti per te. Quando l\'amministrazione '
                                      + 'ne carica uno lo trovi qui.'}
                        </p>
                    ) : (
                        <ul className="adp-lista">
                            {documentiMostrati.map((d) => (
                                <li key={d.id} className="adp-voce">
                                    <FileText size={17} className="adp-icona-file" />
                                    <div className="adp-voce-testo" onClick={() => apri(d)}>
                                        <div className="adp-voce-titolo">
                                            {nomeTipo(d.tipo)}
                                            {d.mese ? ` — ${MESI[d.mese]} ${d.anno}` : ` — ${d.anno}`}
                                        </div>
                                        <div className="adp-voce-sub">
                                            {eAdmin && `${d.nominativo} · `}
                                            {d.titolo}
                                            {eAdmin && ` · ${d.aperture} apertur${d.aperture === 1 ? 'a' : 'e'}`}
                                        </div>
                                    </div>
                                    {eAdmin && (
                                        <>
                                            <button className="adp-icona" title="Chi l'ha aperto"
                                                    onClick={() => vediAccessi(d)}>
                                                <History size={15} />
                                            </button>
                                            <button className="adp-icona" onClick={() => eliminaDoc(d)}>
                                                <Trash2 size={15} />
                                            </button>
                                        </>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </>
            )}

            {/* ============ ORGANICO ============ */}
            {vista === 'organico' && (
                <>
                    <div className="adp-azioni-riga">
                        <button className="adp-btn" onClick={() => apriFormDip(null)}>
                            <Plus size={15} /> Aggiungi all&apos;organico
                        </button>
                    </div>
                    {dipendenti.length === 0 ? (
                        <p className="adp-vuoto">
                            Nessun dipendente. Si aggiunge scegliendo fra gli utenti
                            che hanno già un account.
                        </p>
                    ) : (
                        <table className="adp-tabella">
                            <thead>
                                <tr>
                                    <th>Dipendente</th><th>Matricola</th><th>Assunto</th>
                                    <th>Cessato</th><th className="num">Ore/sett.</th>
                                    <th className="num">Standard</th>
                                    <th>PIN</th><th className="num">Documenti</th><th />
                                </tr>
                            </thead>
                            <tbody>
                                {dipendenti.map((d) => (
                                    <tr key={d.id}>
                                        <td>{d.nominativo}<span className="adp-sub">{d.username}</span></td>
                                        <td>{d.matricola || '—'}</td>
                                        <td>{dataIta(d.assunto_il)}</td>
                                        <td>{d.cessato_il ? dataIta(d.cessato_il) : '—'}</td>
                                        <td className="num">{d.ore_settimanali ? ore(d.ore_settimanali) : '—'}</td>
                                        <td className="num">{d.minuti_standard != null ? minutiVoce(d.minuti_standard) : '—'}</td>
                                        <td>
                                            {!d.ha_pin ? (
                                                <span className="adp-pillola guasto">da impostare</span>
                                            ) : d.pin_bloccato ? (
                                                <span className="adp-pillola guasto">bloccato</span>
                                            ) : d.pin_da_cambiare ? (
                                                <span className="adp-pillola attesa">generato</span>
                                            ) : (
                                                <span className="adp-pillola buono">suo</span>
                                            )}
                                            <button className="adp-icona" title="Genera un PIN nuovo"
                                                    onClick={() => generaPin(d)}>
                                                <KeyRound size={14} />
                                            </button>
                                        </td>
                                        <td className="num">{d.documenti}</td>
                                        <td className="num">
                                            <button className="adp-icona" onClick={() => apriFormDip(d)}>
                                                <Pencil size={15} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </>
            )}

            {/* ============ TABLET ============ */}
            {vista === 'tablet' && (
                <>
                    {impostazioni && (
                        <div className={`adp-interruttore-grande ${impostazioni.attiva ? 'acceso' : ''}`}>
                            <div>
                                <b>
                                    Rilevazione presenze{' '}
                                    {impostazioni.attiva ? 'attiva' : 'non attiva'}
                                </b>
                                <span>
                                    {impostazioni.attiva
                                        ? 'I tablet abilitati possono timbrare.'
                                        : 'Finché è spenta, dal tablet non si può timbrare.'}
                                    {' '}{impostazioni.tablet_abilitati} tablet abilitat
                                    {impostazioni.tablet_abilitati === 1 ? 'o' : 'i'} ·{' '}
                                    {impostazioni.in_organico} in organico
                                    {impostazioni.senza_pin > 0
                                        && ` · ${impostazioni.senza_pin} senza PIN`}
                                </span>
                            </div>
                            <button
                                className={`adp-btn ${impostazioni.attiva ? '' : 'adp-btn--principale'}`}
                                onClick={() => attivaRilevazione(!impostazioni.attiva)}>
                                {impostazioni.attiva ? <><X size={15} /> Disattiva</>
                                                     : <><Check size={15} /> Attiva</>}
                            </button>
                        </div>
                    )}
                    {impostazioni && (
                        <div className="adp-interruttore-grande">
                            <div>
                                <b>
                                    Giornata da {durata(impostazioni.minuti_attesi)},
                                    tolleranza {impostazioni.tolleranza_minuti} minuti
                                </b>
                                <span>
                                    Una giornata fra{' '}
                                    {durata(impostazioni.minuti_attesi - impostazioni.tolleranza_minuti)} e{' '}
                                    {durata(impostazioni.minuti_attesi + impostazioni.tolleranza_minuti)}{' '}
                                    conta come regolare. Sopra è eccedenza, e va autorizzata.
                                    La tolleranza decide <i>se</i> segnalare, non <i>quanto</i>:
                                    a {durata(impostazioni.minuti_attesi + impostazioni.tolleranza_minuti + 1)}
                                    {' '}lo straordinario è di {impostazioni.tolleranza_minuti + 1} minuti,
                                    non di uno.
                                </span>
                            </div>
                            <button className="adp-btn" onClick={() => setFormSoglie({
                                minuti_attesi: String(impostazioni.minuti_attesi),
                                tolleranza_minuti: String(impostazioni.tolleranza_minuti),
                            })}>
                                <Pencil size={15} /> Modifica
                            </button>
                        </div>
                    )}
                    {impostazioni && (
                        <div className="adp-interruttore-grande">
                            <div>
                                <b>
                                    {impostazioni.richieste_giorni
                                        ? `Correzioni chieste entro ${impostazioni.richieste_giorni} `
                                          + `${impostazioni.richieste_giorni === 1 ? 'giorno' : 'giorni'}`
                                        : 'Limite delle correzioni non valido'}
                                </b>
                                <span>
                                    Una dipendente chiede la correzione delle sue timbrature per i
                                    giorni passati, entro questi giorni dalla data; per oggi resta il
                                    tablet. La richiesta la approva o la rifiuta l&apos;amministrazione,
                                    e mai chi l&apos;ha chiesta.
                                </span>
                            </div>
                            <button className="adp-btn" onClick={() => setFormGiorniCorr({
                                giorni: String(impostazioni.richieste_giorni || 7),
                            })}>
                                <Pencil size={15} /> Modifica
                            </button>
                        </div>
                    )}
                    {formGiorniCorr && (
                        <div className="adp-soglie">
                            <label className="adp-campo">
                                <span>Giorni per chiedere una correzione (da 1 a 31)</span>
                                <input type="number" step="1" min="1" max="31" name="richieste_giorni"
                                       value={formGiorniCorr.giorni}
                                       onChange={(e) => setFormGiorniCorr({ giorni: e.target.value })} />
                            </label>
                            <div className="adp-modale-fondo">
                                <button className="adp-btn" onClick={() => setFormGiorniCorr(null)}>Annulla</button>
                                <button className="adp-btn adp-btn--principale" onClick={salvaGiorniCorr}
                                        disabled={inCorso}>
                                    <Check size={15} /> Salva
                                </button>
                            </div>
                        </div>
                    )}
                    {formSoglie && (
                        <div className="adp-soglie">
                            <label className="adp-campo">
                                <span>Minuti attesi per giornata</span>
                                <input type="number" step="1" min="0" max="1440"
                                       value={formSoglie.minuti_attesi}
                                       onChange={(e) => setFormSoglie({
                                           ...formSoglie, minuti_attesi: e.target.value })} />
                            </label>
                            <label className="adp-campo">
                                <span>Tolleranza in minuti</span>
                                <input type="number" step="1" min="0" max="240"
                                       value={formSoglie.tolleranza_minuti}
                                       onChange={(e) => setFormSoglie({
                                           ...formSoglie, tolleranza_minuti: e.target.value })} />
                            </label>
                            <div className="adp-modale-fondo">
                                <button className="adp-btn" onClick={() => setFormSoglie(null)}>Annulla</button>
                                <button className="adp-btn adp-btn--principale" onClick={salvaSoglie}
                                        disabled={inCorso}>
                                    <Check size={15} /> Salva
                                </button>
                            </div>
                            <p className="adp-nota">
                                <b>In minuti interi, non in ore decimali.</b> 6 ore e 15 minuti
                                sono 375, non 6,25: un quarto d&apos;ora in virgola mobile diventa
                                0,2499999, e una giornata risulta incompleta per un secondo.
                            </p>
                        </div>
                    )}
                    <p className="adp-nota">
                        <Tablet size={14} /> Si timbra solo dai dispositivi abilitati qui,
                        con il PIN personale. <b>Non abilitare il tablet della sala
                        d&apos;attesa</b>: lo tocca il paziente. Il controllo è sul server,
                        non sullo schermo: un dispositivo non abilitato riceve un rifiuto
                        anche se qualcuno raggiungesse la pagina.
                    </p>
                    {dispositivi.length === 0 ? (
                        <p className="adp-vuoto">
                            Nessun dispositivo registrato. I tablet si registrano quando si
                            configura la firma dei moduli.
                        </p>
                    ) : (
                        <table className="adp-tabella">
                            <thead>
                                <tr>
                                    <th>Dispositivo</th><th>Tipo</th>
                                    <th>Ultima attività</th><th>Timbratura</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dispositivi.map((d) => (
                                    <tr key={d.id}>
                                        <td>{d.nome}<span className="adp-sub">{d.note || ''}</span></td>
                                        <td>{d.tipo}</td>
                                        <td>{d.ultima_attivita
                                            ? `${dataIta(d.ultima_attivita)} ${oraIta(d.ultima_attivita)}`
                                            : '—'}</td>
                                        <td>
                                            <label className="adp-interruttore">
                                                <input type="checkbox" checked={!!d.usa_timbratura}
                                                       onChange={(e) => abilitaTimbratura(d, e.target.checked)} />
                                                <span>{d.usa_timbratura ? 'abilitato' : 'non abilitato'}</span>
                                            </label>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </>
            )}

            {/* ============ REGISTRO INTERVENTI ============ */}
            {vista === 'storico' && (
                <>
                    <h3 className="adp-sezione">Interventi sul registro</h3>
                    <p className="adp-nota">
                        Ogni timbratura aggiunta, corretta o annullata a mano, col suo
                        motivo. Nulla viene riscritto: se lo studio paghe contesta
                        un&apos;ora, la si ripercorre da qui.
                    </p>
                    {interventi.length === 0 ? (
                        <p className="adp-vuoto">Nessun intervento registrato.</p>
                    ) : (
                        <table className="adp-tabella">
                            <thead>
                                <tr>
                                    <th>Quando</th><th>Chi</th><th>Dipendente</th>
                                    <th>Azione</th><th>Da</th><th>A</th><th>Motivo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {interventi.map((i) => (
                                    <tr key={i.id}>
                                        <td>{dataIta(i.created_at)} {oraIta(i.created_at)}</td>
                                        <td>{i.cognome} {i.nome}</td>
                                        <td>{i.nominativo || '—'}</td>
                                        <td><span className={`adp-pillola ${i.azione}`}>{i.azione}</span></td>
                                        <td>{i.momento_vecchio
                                            ? `${i.tipo_vecchio} ${oraIta(i.momento_vecchio)}` : '—'}</td>
                                        <td>{i.momento_nuovo
                                            ? `${i.tipo_nuovo} ${oraIta(i.momento_nuovo)}` : '—'}</td>
                                        <td className="adp-sub">{i.motivo}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    <h3 className="adp-sezione">Timbrature rifiutate</h3>
                    <p className="adp-nota">
                        Tentativi da indirizzi fuori dalla clinica. Registrati: sapere
                        che qualcuno ha provato è un&apos;informazione.
                    </p>
                    {rifiuti.length === 0 ? (
                        <p className="adp-vuoto">Nessun tentativo rifiutato.</p>
                    ) : (
                        <table className="adp-tabella">
                            <thead>
                                <tr><th>Quando</th><th>Chi</th><th>Da</th><th>Motivo</th></tr>
                            </thead>
                            <tbody>
                                {rifiuti.map((r) => (
                                    <tr key={r.id}>
                                        <td>{dataIta(r.created_at)} {oraIta(r.created_at)}</td>
                                        <td>{r.cognome} {r.nome}</td>
                                        <td>{r.ip}</td>
                                        <td className="adp-sub">{r.motivo}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </>
            )}

            {/* ================= PANNELLI ================= */}

            {/* IL DETTAGLIO DELLA GIORNATA (Alex, 7/10/2026). Tutte le
                timbrature del giorno in ordine di ora, anche quelle corrette
                o annullate (restano, barrate, col perche'); per ognuna da
                dove e' partita e chi e' intervenuto; in fondo il totale e il
                dovuto, gli stessi della griglia. I conti li fa il server. */}
            {pannello === 'giornata' && giornataAperta && (
                <div className="adp-sfondo" onClick={chiudiGiornata}>
                    <div className="adp-modale adp-modale--largo" role="dialog" aria-modal="true"
                         aria-label="Dettaglio della giornata" onClick={(e) => e.stopPropagation()}>
                        <div className="adp-modale-testa">
                            <h2><Clock size={17} /> Dettaglio della giornata</h2>
                            <button onClick={chiudiGiornata} aria-label="Chiudi"><X size={17} /></button>
                        </div>
                        <p className="adp-modale-sub">
                            {giornataAperta.nominativo} · {dataIta(`${giornataAperta.giorno}T12:00:00`)}
                        </p>
                        {giornataAperta.errore && (
                            <p className="adp-errore">
                                <AlertTriangle size={15} /> {giornataAperta.errore}
                            </p>
                        )}
                        {!giornataAperta.dati && !giornataAperta.errore && (
                            <p className="adp-nota">Caricamento…</p>
                        )}
                        {giornataAperta.dati && (() => {
                            const G = giornataAperta.dati;
                            const gg = G.giornata;
                            return (
                                <>
                                    {gg.da_controllare && (
                                        <p className="adp-da-controllare">
                                            <AlertTriangle size={15} /> Da controllare: in questa
                                            giornata c&apos;è una timbratura inserita a mano dalla
                                            dipendente, e la sequenza delle timbrature non torna.
                                        </p>
                                    )}
                                    {G.timbrature.length === 0 ? (
                                        <p className="adp-vuoto">Nessuna timbratura in questa giornata.</p>
                                    ) : (
                                        <div className="rm-scorre">
                                            <table className="adp-tabella adp-giornata">
                                                <thead>
                                                    <tr><th>Ora</th><th>Tipo</th><th>Da dove</th><th>Note</th><th /></tr>
                                                </thead>
                                                <tbody>
                                                    {G.timbrature.map((t) => (
                                                        <tr key={t.id}
                                                            className={`${t.valida ? '' : 'adp-non-valida'}`
                                                                + `${t.dichiarata_il ? ' adp-riga-a-mano' : ''}`}>
                                                            <td className="num">{oraItaliana(t.momento)}</td>
                                                            <td>{t.tipo === 'entrata' ? 'Entrata' : 'Uscita'}</td>
                                                            <td>
                                                                {t.origine === 'tablet'
                                                                    ? (t.tablet || 'tablet')
                                                                    : t.origine === 'admin' ? 'Amministrazione' : t.origine}
                                                            </td>
                                                            <td className="adp-sub">
                                                                {t.dichiarata_il && (
                                                                    <div className="adp-segno-a-mano">
                                                                        ✎ Inserita a mano dalla dipendente
                                                                        alle {oraItaliana(t.dichiarata_il)}, motivo
                                                                        «{t.dichiarata_motivo}»
                                                                    </div>
                                                                )}
                                                                {t.nata_azione === 'inserimento' && (
                                                                    <div>Inserita dall&apos;amministrazione
                                                                        ({t.nata_da || '—'}, {dataOraIta(t.nata_il)}):
                                                                        «{t.nata_motivo}»</div>
                                                                )}
                                                                {t.nata_azione === 'correzione' && (
                                                                    <div>Correzione dell&apos;amministrazione
                                                                        ({t.nata_da || '—'}, {dataOraIta(t.nata_il)}):
                                                                        «{t.nata_motivo}»</div>
                                                                )}
                                                                {t.uscita_azione === 'annullamento' && (
                                                                    <div>Annullata dall&apos;amministrazione
                                                                        ({t.uscita_da || '—'}, {dataOraIta(t.uscita_il)}):
                                                                        «{t.uscita_motivo}»</div>
                                                                )}
                                                                {t.uscita_azione === 'correzione' && (
                                                                    <div>Corretta dall&apos;amministrazione
                                                                        ({t.uscita_da || '—'}, {dataOraIta(t.uscita_il)}):
                                                                        «{t.uscita_motivo}»</div>
                                                                )}
                                                                {t.segnale && (
                                                                    <span className={`adp-segnale ${t.segnale}`}>
                                                                        {t.segnale_testo}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="adp-giornata-azioni">
                                                                {t.valida && G.puo_correggere !== false && (
                                                                    <>
                                                                        <button className="adp-icona" title="Correggi"
                                                                                onClick={() => intervieni('correzione', t)}>
                                                                            <Pencil size={14} />
                                                                        </button>
                                                                        <button className="adp-icona" title="Annulla"
                                                                                onClick={() => intervieni('annullamento', t)}>
                                                                            <Trash2 size={14} />
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                    <div className="adp-giornata-totali">
                                        <div><span>Totale lavorato</span>
                                             <b>{gg.minuti ? durata(gg.minuti) : '0 min'}</b></div>
                                        <div><span>Dovuto</span>
                                             <b>{gg.attesi ? durata(gg.attesi) : '0 min'}</b></div>
                                        <div><span>Stato</span>
                                             <b>{(STATI[gg.stato] || {}).t || gg.etichetta}</b></div>
                                        {gg.ordinarie != null && (
                                            <>
                                                <div><span>Ordinarie</span>
                                                     <b>{minutiVoce(gg.ordinarie)}</b></div>
                                                <div><span>Straordinario autorizzato</span>
                                                     <b>{minutiVoce(gg.straordinario_autorizzato)}</b></div>
                                                <div><span>Straordinario da autorizzare</span>
                                                     <b>{minutiVoce(gg.straordinario_da_autorizzare)}</b></div>
                                                <div><span>Ore mancanti</span>
                                                     <b>{minutiVoce(gg.mancanti)}</b></div>
                                            </>
                                        )}
                                    </div>
                                    {gg.corretta && (
                                        <p className="adp-nota adp-nota-corretta" data-corretta>
                                            {testoCorretta(gg.corretta)}
                                        </p>
                                    )}
                                    {G.puo_correggere === false && (
                                        /* Chi ha timbrato non corregge (340): il motivo lo
                                           manda il server, e il controllo vero e' suo. */
                                        <p className="adp-nota" data-non-corregge>{G.non_corregge_perche}</p>
                                    )}
                                    {gg.festa && <p className="adp-nota">Festivo: {gg.festa}</p>}
                                    {gg.nota && <p className="adp-nota">{gg.nota}</p>}
                                    {gg.sigla && (
                                        <p className="adp-nota">Assenza: {gg.sigla}{gg.causale ? ` — ${gg.causale}` : ''}</p>
                                    )}
                                </>
                            );
                        })()}
                        <div className="adp-modale-fondo">
                            <button className="adp-btn" onClick={chiudiGiornata}>Chiudi</button>
                            {giornataAperta.dati?.puo_correggere !== false && (
                                <button className="adp-btn adp-btn--principale"
                                        onClick={() => intervieni('inserimento', null)}
                                        disabled={!giornataAperta.dati}>
                                    <Plus size={15} /> Aggiungi una timbratura
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {moduloCorr && (
                <ModuloCorrezione giorno={moduloCorr.giorno} entroGiorni={mieCorr.entro_giorni}
                                  dipendenteId={eAdmin ? stato?.dipendente_id : undefined}
                                  onChiudi={() => setModuloCorr(null)}
                                  onInviata={correzioneInviata} />
            )}

            {pannello === 'timbratura' && formTimbratura && (
                <div className="adp-sfondo" onClick={() => setPannello(giornataAperta ? 'giornata' : null)}>
                    <div className="adp-modale" onClick={(e) => e.stopPropagation()}>
                        <div className="adp-modale-testa">
                            <h2>{formTimbratura.azione === 'correzione' ? 'Correggi una timbratura'
                                 : formTimbratura.azione === 'annullamento' ? 'Annulla una timbratura'
                                 : 'Aggiungi una timbratura'}</h2>
                            <button onClick={() => setPannello(giornataAperta ? 'giornata' : null)}><X size={17} /></button>
                        </div>
                        <p className="adp-modale-sub">
                            {formTimbratura.nominativo} · {dataIta(`${formTimbratura.giorno}T12:00:00`)}
                            {formTimbratura.vecchia && <> · {formTimbratura.vecchia}</>}
                        </p>
                        {formTimbratura.azione !== 'annullamento' && (<>
                        <label className="adp-campo">
                            <span>Momento *</span>
                            <input type="datetime-local" value={formTimbratura.momento}
                                   onChange={(e) => setFormTimbratura({ ...formTimbratura, momento: e.target.value })} />
                        </label>
                        <label className="adp-campo">
                            <span>Tipo *</span>
                            <select value={formTimbratura.tipo}
                                    onChange={(e) => setFormTimbratura({ ...formTimbratura, tipo: e.target.value })}>
                                <option value="entrata">Entrata</option>
                                <option value="uscita">Uscita</option>
                            </select>
                        </label>
                        </>)}
                        <label className="adp-campo">
                            <span>Motivo *</span>
                            <textarea rows="2" value={formTimbratura.motivo}
                                      placeholder={formTimbratura.azione === 'inserimento'
                                          ? 'Perché la stai aggiungendo a mano'
                                          : formTimbratura.azione === 'correzione'
                                              ? 'Perché la stai correggendo' : 'Perché la stai annullando'}
                                      onChange={(e) => setFormTimbratura({ ...formTimbratura, motivo: e.target.value })} />
                        </label>
                        <p className="adp-nota">
                            {formTimbratura.azione === 'inserimento'
                                ? 'Il motivo è obbligatorio e resta nel registro insieme al tuo nome. '
                                  + 'La timbratura risulterà inserita a mano, non timbrata.'
                                : 'Il motivo è obbligatorio e resta nel registro insieme al tuo nome. '
                                  + 'La timbratura di prima non si cancella: resta nel dettaglio, barrata.'}
                        </p>
                        <div className="adp-modale-fondo">
                            <button className="adp-btn" onClick={() => setPannello(giornataAperta ? 'giornata' : null)}>
                                {giornataAperta ? 'Indietro' : 'Annulla'}
                            </button>
                            <button className="adp-btn adp-btn--principale" onClick={salvaTimbratura}
                                    disabled={inCorso || !formTimbratura.motivo.trim() || !formTimbratura.momento}>
                                <Check size={15} /> Registra
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {pannello === 'assenza' && formAssenza && (
                <div className="adp-sfondo" onClick={() => setPannello(null)}>
                    <div className="adp-modale" onClick={(e) => e.stopPropagation()}>
                        <div className="adp-modale-testa">
                            <h2><CalendarOff size={17} /> Ferie, permesso o malattia</h2>
                            <button onClick={() => setPannello(null)}><X size={17} /></button>
                        </div>
                        <label className="adp-campo">
                            <span>Dipendente *</span>
                            <select value={formAssenza.dipendente_id}
                                    onChange={(e) => setFormAssenza({ ...formAssenza, dipendente_id: e.target.value })}>
                                <option value="">Scegli…</option>
                                {dipendenti.map((d) => (
                                    <option key={d.id} value={d.id}>{d.nominativo}</option>
                                ))}
                            </select>
                        </label>
                        <label className="adp-campo">
                            <span>Causale *</span>
                            <select value={formAssenza.causale_id}
                                    onChange={(e) => setFormAssenza({ ...formAssenza, causale_id: e.target.value, ore: '' })}>
                                <option value="">Scegli…</option>
                                {causali.map((c) => (
                                    <option key={c.id} value={c.id}>{c.sigla} — {c.descrizione}</option>
                                ))}
                            </select>
                        </label>
                        <div className="adp-riga">
                            <label className="adp-campo">
                                <span>Dal *</span>
                                <input type="date" value={formAssenza.dal}
                                       onChange={(e) => setFormAssenza({ ...formAssenza, dal: e.target.value })} />
                            </label>
                            <label className="adp-campo">
                                <span>Al</span>
                                <input type="date" value={formAssenza.al}
                                       onChange={(e) => setFormAssenza({ ...formAssenza, al: e.target.value })} />
                            </label>
                        </div>
                        {(() => {
                            const c = causali.find((x) => String(x.id) === String(formAssenza.causale_id));
                            if (!c?.ammette_ore) return null;
                            return (
                                <>
                                    <label className="adp-campo">
                                        <span>Ore (vuoto = giornata intera)</span>
                                        <input type="number" step="0.25" min="0.25" max="24"
                                               value={formAssenza.ore}
                                               onChange={(e) => setFormAssenza({ ...formAssenza, ore: e.target.value })} />
                                    </label>
                                    <p className="adp-nota">
                                        Un permesso a ore vale per un solo giorno, e convive con
                                        le ore lavorate: nell&apos;export compare come
                                        «{c.sigla} 2» accanto alle ore fatte.
                                    </p>
                                </>
                            );
                        })()}
                        <label className="adp-campo">
                            <span>Note</span>
                            <input value={formAssenza.note}
                                   onChange={(e) => setFormAssenza({ ...formAssenza, note: e.target.value })} />
                        </label>
                        <div className="adp-modale-fondo">
                            <button className="adp-btn" onClick={() => setPannello(null)}>Annulla</button>
                            <button className="adp-btn adp-btn--principale" onClick={salvaAssenza}
                                    disabled={inCorso || !formAssenza.dipendente_id
                                              || !formAssenza.causale_id || !formAssenza.dal}>
                                <Check size={15} /> Registra
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ---- il dipendente CHIEDE ----
                Nessun campo «dipendente»: non si sceglie, lo decide il
                server. Se ci fosse, chiunque potrebbe chiedere ferie per
                un collega - il corpo della richiesta arriva dal browser,
                e il browser e' di chi lo usa. */}
            {pannello === 'richiesta' && formRichiesta && (
                <div className="adp-sfondo" onClick={() => setPannello(null)}>
                    <div className="adp-modale" onClick={(e) => e.stopPropagation()}>
                        <div className="adp-modale-testa">
                            <h2><Send size={17} /> Chiedi ferie o permesso</h2>
                            <button onClick={() => setPannello(null)}><X size={17} /></button>
                        </div>
                        <label className="adp-campo">
                            <span>Causale *</span>
                            <select value={formRichiesta.causale_id}
                                    onChange={(e) => setFormRichiesta({
                                        ...formRichiesta, causale_id: e.target.value, ore: '' })}>
                                <option value="">Scegli…</option>
                                {causali.map((c) => (
                                    <option key={c.id} value={c.id}>{c.sigla} — {c.descrizione}</option>
                                ))}
                            </select>
                        </label>
                        <div className="adp-riga">
                            <label className="adp-campo">
                                <span>Dal *</span>
                                <input type="date" value={formRichiesta.dal}
                                       onChange={(e) => setFormRichiesta({ ...formRichiesta, dal: e.target.value })} />
                            </label>
                            <label className="adp-campo">
                                <span>Al</span>
                                <input type="date" value={formRichiesta.al}
                                       onChange={(e) => setFormRichiesta({ ...formRichiesta, al: e.target.value })} />
                            </label>
                        </div>
                        {(() => {
                            const c = causali.find((x) => String(x.id) === String(formRichiesta.causale_id));
                            if (!c?.ammette_ore) return null;
                            return (
                                <label className="adp-campo">
                                    <span>Ore (vuoto = giornata intera)</span>
                                    <input type="number" step="0.25" min="0.25" max="24"
                                           value={formRichiesta.ore}
                                           onChange={(e) => setFormRichiesta({ ...formRichiesta, ore: e.target.value })} />
                                </label>
                            );
                        })()}
                        <label className="adp-campo">
                            <span>Note</span>
                            <input value={formRichiesta.note}
                                   placeholder="Il motivo, se serve dirlo"
                                   onChange={(e) => setFormRichiesta({ ...formRichiesta, note: e.target.value })} />
                        </label>
                        <p className="adp-nota">
                            La richiesta resta <b>in attesa</b> finché l&apos;amministrazione non
                            risponde: finché è così, quei giorni continuano a contare come
                            giorni da lavorare. Puoi ritirarla finché nessuno ha deciso.
                        </p>
                        <div className="adp-modale-fondo">
                            <button className="adp-btn" onClick={() => setPannello(null)}>Annulla</button>
                            <button className="adp-btn adp-btn--principale" onClick={inviaRichiesta}
                                    disabled={inCorso || !formRichiesta.causale_id || !formRichiesta.dal}>
                                <Send size={15} /> Invia la richiesta
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ---- la decisione ----
                Il motivo del rifiuto e' obbligatorio, e il vincolo sta nel
                database: un'assenza si puo' respingere da piu' di una
                strada, e un controllo solo qui sarebbe un controllo che
                ogni strada nuova deve ricordarsi di rifare. */}
            {pannello === 'decisione' && formDecisione && (
                <div className="adp-sfondo" onClick={() => setPannello(null)}>
                    <div className="adp-modale" onClick={(e) => e.stopPropagation()}>
                        <div className="adp-modale-testa">
                            <h2>
                                {formDecisione.stato === 'approvata'
                                    ? <><ThumbsUp size={17} /> Approva la richiesta</>
                                    : <><ThumbsDown size={17} /> Respingi la richiesta</>}
                            </h2>
                            <button onClick={() => setPannello(null)}><X size={17} /></button>
                        </div>
                        <p className="adp-riepilogo-testo">
                            <b>{formDecisione.nominativo}</b> — {formDecisione.sigla}{' '}
                            {formDecisione.descrizione}, dal {dataIta(formDecisione.dal)}{' '}
                            al {dataIta(formDecisione.al)}
                            {formDecisione.ore ? ` (${ore(formDecisione.ore)} ore)` : ''}.
                        </p>
                        {formDecisione.gia_passata && (
                            <p className="adp-nota adp-nota--attenzione">
                                Questi giorni sono già passati: quella che stai per prendere
                                non è un&apos;autorizzazione, è una registrazione.
                            </p>
                        )}
                        {formDecisione.stato === 'respinta' && (
                            <label className="adp-campo">
                                <span>Motivo del rifiuto *</span>
                                <textarea rows={3} value={formDecisione.motivo_rifiuto}
                                          placeholder="Lo legge chi ha fatto la richiesta"
                                          onChange={(e) => setFormDecisione({
                                              ...formDecisione, motivo_rifiuto: e.target.value })} />
                            </label>
                        )}
                        {formDecisione.stato === 'respinta' && (
                            <p className="adp-nota">
                                Un rifiuto senza motivo è una decisione che nessuno può
                                discutere, e su ferie e permessi la discussione è
                                esattamente il punto.
                            </p>
                        )}
                        {formDecisione.stato === 'approvata' && (
                            <p className="adp-nota">
                                Da ora quei giorni sostituiscono le ore attese, e compaiono
                                nell&apos;export per lo studio paghe.
                            </p>
                        )}
                        <div className="adp-modale-fondo">
                            <button className="adp-btn" onClick={() => setPannello(null)}>Annulla</button>
                            <button className="adp-btn adp-btn--principale" onClick={decidi}
                                    disabled={inCorso || (formDecisione.stato === 'respinta'
                                              && !formDecisione.motivo_rifiuto.trim())}>
                                <Check size={15} />
                                {formDecisione.stato === 'approvata' ? ' Approva' : ' Respingi'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ---- autorizzazione allo straordinario ---- */}
            {pannello === 'straordinario' && formStraordinario && (
                <div className="adp-sfondo" onClick={() => setPannello(null)}>
                    <div className="adp-modale" onClick={(e) => e.stopPropagation()}>
                        <div className="adp-modale-testa">
                            <h2><Timer size={17} /> Autorizza straordinario</h2>
                            <button onClick={() => setPannello(null)}><X size={17} /></button>
                        </div>
                        {formStraordinario.nominativo ? (
                            <p className="adp-riepilogo-testo"><b>{formStraordinario.nominativo}</b></p>
                        ) : (
                            <label className="adp-campo">
                                <span>Dipendente *</span>
                                <select value={formStraordinario.dipendente_id}
                                        onChange={(e) => setFormStraordinario({
                                            ...formStraordinario, dipendente_id: e.target.value })}>
                                    <option value="">Scegli…</option>
                                    {dipendenti.map((d) => (
                                        <option key={d.id} value={d.id}>{d.nominativo}</option>
                                    ))}
                                </select>
                            </label>
                        )}
                        <div className="adp-riga">
                            <label className="adp-campo">
                                <span>Dal *</span>
                                <input type="date" value={formStraordinario.dal}
                                       onChange={(e) => setFormStraordinario({
                                           ...formStraordinario, dal: e.target.value })} />
                            </label>
                            <label className="adp-campo">
                                <span>Al</span>
                                <input type="date" value={formStraordinario.al}
                                       onChange={(e) => setFormStraordinario({
                                           ...formStraordinario, al: e.target.value })} />
                            </label>
                        </div>
                        <label className="adp-campo">
                            <span>Minuti al giorno *</span>
                            <input type="number" step="1" min="1" max="1440"
                                   value={formStraordinario.minuti}
                                   onChange={(e) => setFormStraordinario({
                                       ...formStraordinario, minuti: e.target.value })} />
                        </label>
                        {/* Cosa si sta per coprire, con i numeri di quella
                            persona in quel periodo. Un'autorizzazione concessa
                            alla cieca e' un'autorizzazione che non si sa se
                            basta, e il compito notturno lo direbbe la notte
                            dopo - che e' tardi per accorgersene. */}
                        {formStraordinario.copre && (() => {
                            const n = parseInt(formStraordinario.minuti, 10) || 0;
                            const c = formStraordinario.copre;
                            const coperte = n >= c.minuti ? c.giornate : null;
                            return (
                                <p className={`adp-copertura ${coperte ? 'buono' : 'attesa'}`}>
                                    {coperte
                                        ? `Copre tutte le ${c.giornate} giornate scoperte del `
                                          + `periodo (${durata(c.totale)} in totale).`
                                        : `Con ${durata(n)} al giorno restano scoperte le `
                                          + `giornate oltre questa soglia: ne servono `
                                          + `${durata(c.minuti)} per coprirle tutte e ${c.giornate}.`}
                                </p>
                            );
                        })()}
                        <label className="adp-campo">
                            <span>Motivo</span>
                            <input value={formStraordinario.motivo}
                                   placeholder="Perché si resta oltre l'orario"
                                   onChange={(e) => setFormStraordinario({
                                       ...formStraordinario, motivo: e.target.value })} />
                        </label>
                        <p className="adp-nota">
                            <b>Minuti interi</b>, e valgono <b>per ogni giorno</b> del periodo:
                            «mezz&apos;ora tutta la settimana» sono 30 minuti dal lunedì al venerdì,
                            non 30 in tutto. Due autorizzazioni che si sovrappongono si sommano.
                        </p>
                        <div className="adp-modale-fondo">
                            <button className="adp-btn" onClick={() => setPannello(null)}>Annulla</button>
                            <button className="adp-btn adp-btn--principale"
                                    onClick={autorizzaStraordinario}
                                    disabled={inCorso || !formStraordinario.dipendente_id
                                              || !formStraordinario.dal
                                              || !(parseInt(formStraordinario.minuti, 10) > 0)}>
                                <Check size={15} /> Autorizza
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {pannello === 'documento' && formDoc && (
                <div className="adp-sfondo" onClick={() => setPannello(null)}>
                    <div className="adp-modale" onClick={(e) => e.stopPropagation()}>
                        <div className="adp-modale-testa">
                            <h2>Carica un documento</h2>
                            <button onClick={() => setPannello(null)}><X size={17} /></button>
                        </div>
                        <label className="adp-campo">
                            <span>Destinatario *</span>
                            <select value={formDoc.dipendente_id}
                                    onChange={(e) => setFormDoc({ ...formDoc, dipendente_id: e.target.value })}>
                                <option value="">Scegli…</option>
                                {dipendenti.map((d) => (
                                    <option key={d.id} value={d.id}>{d.nominativo}</option>
                                ))}
                            </select>
                        </label>
                        <div className="adp-riga">
                            <label className="adp-campo">
                                <span>Tipo *</span>
                                <select value={formDoc.tipo}
                                        onChange={(e) => setFormDoc({ ...formDoc, tipo: e.target.value })}>
                                    {TIPI_DOC_GENERICI.map((t) => <option key={t.v} value={t.v}>{t.t}</option>)}
                                </select>
                            </label>
                            <label className="adp-campo">
                                <span>Anno *</span>
                                <input type="number" min="2000" max="2100" value={formDoc.anno}
                                       onChange={(e) => setFormDoc({ ...formDoc, anno: e.target.value })} />
                            </label>
                            <label className="adp-campo">
                                <span>Mese{formDoc.tipo === 'busta_paga' ? ' *' : ''}</span>
                                <select value={formDoc.mese}
                                        onChange={(e) => setFormDoc({ ...formDoc, mese: e.target.value })}>
                                    <option value="">—</option>
                                    {MESI.slice(1).map((m, i) => (
                                        <option key={i + 1} value={i + 1}>{m}</option>
                                    ))}
                                </select>
                            </label>
                        </div>
                        <label className="adp-campo">
                            <span>File * (PDF o immagine)</span>
                            <input type="file" accept="application/pdf,image/*"
                                   onChange={(e) => setFormDoc({ ...formDoc, file: e.target.files?.[0] || null })} />
                        </label>
                        <label className="adp-campo">
                            <span>Note</span>
                            <input value={formDoc.note}
                                   onChange={(e) => setFormDoc({ ...formDoc, note: e.target.value })} />
                        </label>
                        <p className="adp-nota">
                            Il documento sarà visibile solo al destinatario e a te. Ogni
                            apertura viene registrata.
                        </p>
                        <div className="adp-modale-fondo">
                            <button className="adp-btn" onClick={() => setPannello(null)}>Annulla</button>
                            <button className="adp-btn adp-btn--principale" onClick={salvaDoc}
                                    disabled={inCorso || !formDoc.file || !formDoc.dipendente_id
                                              || (formDoc.tipo === 'busta_paga' && !formDoc.mese)}>
                                <Check size={15} /> Carica
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {pannello === 'dipendente' && formDip && (
                <div className="adp-sfondo" onClick={() => setPannello(null)}>
                    <div className="adp-modale" onClick={(e) => e.stopPropagation()}>
                        <div className="adp-modale-testa">
                            <h2><Users size={17} /> {formDip.id ? 'Modifica dipendente' : 'Aggiungi all\'organico'}</h2>
                            <button onClick={() => setPannello(null)}><X size={17} /></button>
                        </div>
                        {formDip.id ? (
                            <p className="adp-modale-sub">{formDip.nominativo}</p>
                        ) : (
                            <label className="adp-campo">
                                <span>Utente *</span>
                                <select value={formDip.utente_id}
                                        onChange={(e) => setFormDip({ ...formDip, utente_id: e.target.value })}>
                                    <option value="">Scegli…</option>
                                    {candidati.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.cognome} {c.nome} ({c.ruolo})
                                        </option>
                                    ))}
                                </select>
                            </label>
                        )}
                        <div className="adp-riga">
                            <label className="adp-campo">
                                <span>Assunto il *</span>
                                <input type="date" value={String(formDip.assunto_il || '').slice(0, 10)}
                                       onChange={(e) => setFormDip({ ...formDip, assunto_il: e.target.value })} />
                            </label>
                            <label className="adp-campo">
                                <span>Cessato il</span>
                                <input type="date" value={String(formDip.cessato_il || '').slice(0, 10)}
                                       onChange={(e) => setFormDip({ ...formDip, cessato_il: e.target.value })} />
                            </label>
                        </div>
                        <div className="adp-riga">
                            <label className="adp-campo">
                                <span>Matricola</span>
                                <input value={formDip.matricola || ''}
                                       onChange={(e) => setFormDip({ ...formDip, matricola: e.target.value })} />
                            </label>
                            <label className="adp-campo">
                                <span>Ore settimanali</span>
                                <input type="number" step="0.5" value={formDip.ore_settimanali || ''}
                                       onChange={(e) => setFormDip({ ...formDip, ore_settimanali: e.target.value })} />
                            </label>
                        </div>
                        <div className="adp-riga">
                            {/* Lo standard della giornata (Alex, 2/11/2026;
                                migration 351): le prime ore fino a qui sono
                                ordinarie, il resto straordinario. In minuti,
                                come le impostazioni: 375 = 6h15. Vuoto su un
                                dipendente nuovo: vale lo standard di serie. */}
                            <label className="adp-campo">
                                <span>Standard della giornata (minuti)</span>
                                <input type="number" step="1" min="0" max="720"
                                       placeholder="375 = 6h15"
                                       value={formDip.minuti_standard ?? ''}
                                       onChange={(e) => setFormDip({ ...formDip, minuti_standard: e.target.value })} />
                            </label>
                        </div>
                        <p className="adp-nota">
                            La cessazione non disattiva l&apos;account: sono due cose diverse.
                            Chi cessa continua a comparire nell&apos;export del periodo in cui
                            ha lavorato, e le sue buste paga restano consultabili.
                        </p>
                        <div className="adp-modale-fondo">
                            <button className="adp-btn" onClick={() => setPannello(null)}>Annulla</button>
                            <button className="adp-btn adp-btn--principale" onClick={salvaDip}
                                    disabled={inCorso || !formDip.assunto_il
                                              || (!formDip.id && !formDip.utente_id)}>
                                <Check size={15} /> Salva
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {pannello === 'pin' && formPin && (
                <div className="adp-sfondo" onClick={() => setPannello(null)}>
                    <div className="adp-modale" onClick={(e) => e.stopPropagation()}>
                        <div className="adp-modale-testa">
                            <h2><KeyRound size={17} /> Il tuo PIN</h2>
                            <button onClick={() => setPannello(null)}><X size={17} /></button>
                        </div>
                        <p className="adp-nota">
                            <Tablet size={14} /> Quattro cifre, servono a timbrare dal
                            tablet della segreteria. Nessuno può leggerlo, nemmeno
                            l&apos;amministrazione: se lo dimentichi te ne fanno generare
                            uno nuovo.
                        </p>
                        {statoPin?.impostato && (
                            <label className="adp-campo">
                                <span>PIN attuale *</span>
                                <input type="password" inputMode="numeric" maxLength="4"
                                       autoComplete="off" value={formPin.attuale}
                                       onChange={(e) => setFormPin({ ...formPin,
                                           attuale: e.target.value.replace(/\D/g, '') })} />
                            </label>
                        )}
                        <div className="adp-riga">
                            <label className="adp-campo">
                                <span>PIN nuovo *</span>
                                <input type="password" inputMode="numeric" maxLength="4"
                                       autoComplete="off" value={formPin.nuovo}
                                       onChange={(e) => setFormPin({ ...formPin,
                                           nuovo: e.target.value.replace(/\D/g, '') })} />
                            </label>
                            <label className="adp-campo">
                                <span>Ripetilo *</span>
                                <input type="password" inputMode="numeric" maxLength="4"
                                       autoComplete="off" value={formPin.conferma}
                                       onChange={(e) => setFormPin({ ...formPin,
                                           conferma: e.target.value.replace(/\D/g, '') })} />
                            </label>
                        </div>
                        {formPin.nuovo && formPin.conferma && formPin.nuovo !== formPin.conferma && (
                            <p className="adp-nota adp-nota--attenzione">I due PIN non coincidono.</p>
                        )}
                        <div className="adp-modale-fondo">
                            <button className="adp-btn" onClick={() => setPannello(null)}>Annulla</button>
                            <button className="adp-btn adp-btn--principale" onClick={salvaPin}
                                    disabled={inCorso || formPin.nuovo.length !== 4
                                              || formPin.nuovo !== formPin.conferma
                                              || (statoPin?.impostato && formPin.attuale.length !== 4)}>
                                <Check size={15} /> Salva
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {pinGenerato && (
                <div className="adp-sfondo" onClick={() => setPinGenerato(null)}>
                    <div className="adp-modale" onClick={(e) => e.stopPropagation()}>
                        <div className="adp-modale-testa">
                            <h2><KeyRound size={17} /> PIN generato</h2>
                            <button onClick={() => setPinGenerato(null)}><X size={17} /></button>
                        </div>
                        <p className="adp-modale-sub">{pinGenerato.nominativo}</p>
                        <div className="adp-pin-grande">{pinGenerato.pin}</div>
                        <p className="adp-nota adp-nota--attenzione">
                            <AlertTriangle size={14} /> Annotalo e comunicalo alla persona:
                            non verrà più mostrato. Da adesso nel gestionale resta solo
                            una forma cifrata, quindi si può rigenerare ma non recuperare.
                            Consigliale di cambiarlo dalla sua pagina Presenze.
                        </p>
                        <div className="adp-modale-fondo">
                            <button className="adp-btn adp-btn--principale"
                                    onClick={() => setPinGenerato(null)}>
                                <Check size={15} /> Annotato
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {accessi && (
                <div className="adp-sfondo" onClick={() => setAccessi(null)}>
                    <div className="adp-modale" onClick={(e) => e.stopPropagation()}>
                        <div className="adp-modale-testa">
                            <h2><History size={17} /> Chi ha aperto questo documento</h2>
                            <button onClick={() => setAccessi(null)}><X size={17} /></button>
                        </div>
                        <p className="adp-modale-sub">
                            {nomeTipo(accessi.documento.tipo)} · {accessi.documento.nominativo}
                        </p>
                        {accessi.righe.length === 0 ? (
                            <p className="adp-vuoto">Nessuno l&apos;ha ancora aperto.</p>
                        ) : (
                            <table className="adp-tabella">
                                <thead>
                                    <tr><th>Quando</th><th>Chi</th><th>Ruolo</th><th>Da</th></tr>
                                </thead>
                                <tbody>
                                    {accessi.righe.map((a, i) => (
                                        <tr key={i}>
                                            <td>{dataIta(a.created_at)} {oraIta(a.created_at)}</td>
                                            <td>{a.cognome} {a.nome}</td>
                                            <td>{a.ruolo}</td>
                                            <td className="adp-sub">{a.ip}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AreaDipendenti;
